// FloorLayoutEditor — ChatGPT-vision floor-plan parser + SVG geometry editor.
//
// Route: /dashboard/sites/:siteId/buildings/:buildingId/floors/:floorId/layout
//
// Pipeline:
//   1. User uploads PNG / JPG / PDF (drag-drop or file picker, max 10 MB).
//   2. Frontend POSTs multipart to /v1/floors/:id/layout-parse → backend
//      uploads to R2 + calls gpt-4o vision + writes normalized_json onto a
//      fresh draft layout_version.
//   3. Editor renders the parsed walls / rooms / doors / corridors / entry
//      points / labels as editable SVG elements.
//   4. User can drag endpoints, add new entities, delete selected ones,
//      toggle layer visibility, undo / redo (30 step history). Save sends
//      the edited normalized JSON back to the same version row.
//
// Mobile (≤ 480 px): read-only view + minor edits (drag-only). Toolbar
// collapses to bottom sheet.
//
// Accent palette stays SENTRY brand (cyan #00d4ff on near-black) — see
// Tailwind tokens in apps/frontend/tailwind.config.cjs.

import React, {
  Suspense, lazy,
  useCallback, useEffect, useMemo, useReducer, useRef, useState,
} from "react";

// 3D viewer lives in a separate chunk — only loaded when the user clicks
// the "3D" toggle. three.js + @react-three/fiber + @react-three/drei
// add ~280 KB gzipped, which we don't want to inflict on 2D-only sessions.
const FloorLayout3D = lazy(() => import("../floor-stack/three/FloorLayout3D.js"));
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Upload, Save, RefreshCw, Download, Undo2, Redo2,
  MousePointer2, Pencil, Square, DoorClosed, Trash2, ZoomIn, ZoomOut, Layers,
  Loader2, AlertTriangle, CheckCircle2, Tag, X, Box, Map as MapIcon,
  SquareDashedMousePointer, ChevronDown,
} from "lucide-react";
import {
  reparseLayoutVersion, saveLayoutEdits,
  getFloorLayout, friendlyFloorStackError,
  EMPTY_LAYOUT,
  listMarkers, createMarker, patchMarker, archiveMarker, clearFloorLayout,
  type NormalizedLayoutDto, type LayoutPoint, type LayoutWall,
  type LayoutRoom, type LayoutDoor,
  type MapMarkerDto,
} from "../lib/floorStackApi.js";
import { createMapImport, MapImportApiError } from "../lib/mapImportApi.js";
import { listLocations, type LocationRow } from "../lib/locationsApi.js";
import { useDashboardSession } from "../auth/useDashboardSession.js";
import { useConfirmDialog } from "../components/ConfirmDialog.js";

const HISTORY_DEPTH = 30;

/**
 * gpt-5.5 typical wall-clock for a 1–2 MB floor plan is 25–40 s. Used by
 * the elapsed-time display to size the indeterminate progress bar — the
 * UI shows "已等候 X / 預估 Y 秒" so the user has a sense of pacing.
 */
const AI_EXPECTED_SECONDS = 30;

/**
 * Marker visual palette. Operators have asked the markers layer to be
 * the most prominent thing on the canvas (they're acting on it), so
 * every type uses high-contrast solid fills + dark stroke. Type
 * indicator is a single Chinese / Latin glyph so it reads at a glance
 * even before the per-marker label loads.
 */
function markerColours(kind: string): { fill: string; stroke: string; fg: string } {
  switch (kind) {
    case "location": return { fill: "#00d4ff", stroke: "#0b0d12", fg: "#0b0d12" };
    case "camera":     return { fill: "#a78bfa", stroke: "#0b0d12", fg: "#0b0d12" };
    case "aed":        return { fill: "#f87171", stroke: "#0b0d12", fg: "#ffffff" };
    case "exit":       return { fill: "#34d399", stroke: "#0b0d12", fg: "#0b0d12" };
    default:           return { fill: "#fbbf24", stroke: "#0b0d12", fg: "#0b0d12" };
  }
}

function markerTypeIcon(kind: string): { short: string; label: string } {
  switch (kind) {
    case "location": return { short: "T", label: "地點" };
    case "camera":     return { short: "C", label: "監視器" };
    case "aed":        return { short: "+", label: "AED" };
    case "exit":       return { short: "→", label: "出口" };
    default:           return { short: "?", label: kind };
  }
}

/**
 * Map a thrown MapImportApiError (or any other exception thrown by the
 * createMapImport upload XHR) to a zh-TW toast. The async pipeline only
 * sees the upload phase here — anything downstream surfaces on
 * /dashboard/map-imports/:id — so this stays focused on multipart
 * upload + validation errors.
 */
function friendlyCreateImportError(err: unknown): string {
  if (err instanceof MapImportApiError) {
    if (err.code === "aborted") return "已取消上傳。";
    if (err.code === "network" || err.status === 0) return "網路連線中斷，請檢查連線後再上傳。";
    if (err.status === 413 || err.code === "file_too_large") return "圖片太大（超過 25 MB），請先壓縮再上傳。";
    if (err.status === 415 || err.code === "unsupported_mime") return "目前只支援 PNG / JPG / WebP / PDF 四種格式。";
    if (err.status === 400 && err.code === "missing_file") return "沒有偵測到上傳的圖檔，請重新選一張。";
    if (err.status === 400 && err.code === "empty_file") return "圖檔內容是空的，請改用其他檔案。";
    if (err.status === 400 && err.code === "floor_not_found") return "找不到這層樓的資料，可能已被刪除，請回上一頁重新整理。";
    if (err.status === 401) return "登入狀態已逾時，請重新登入後再試。";
    if (err.status === 403) return "您目前的角色不能匯入此案場的樓層圖。";
    if (err.status === 429) return "目前匯入流量過大，請等 1 分鐘後再重新上傳。";
    if (err.status === 503 && err.code === "map_imports_bucket_unbound") return "後端儲存空間尚未配置完成，請通知系統管理員。";
    if (err.status === 503 && err.code === "queue_unbound") return "後端佇列尚未配置完成，請通知系統管理員。";
  }
  return "圖檔上傳失敗，請稍後再試。";
}

/**
 * Map a thrown FloorStackApiError (or any exception) to a stage-specific
 * zh-TW message. Keep these one short sentence each — the editor renders
 * them as toast strings and as an error banner inside the upload overlay.
 */
function friendlyParseStageError(stage: "upload" | "ai", err: unknown): string {
  // Network / abort / browser-side failures.
  if (err && typeof err === "object" && "code" in err) {
    const code = (err as { code?: string }).code;
    const status = (err as { status?: number }).status;
    if (code === "aborted") return "已取消上傳。";
    if (code === "network" || status === 0) return "網路連線中斷，請檢查連線後再上傳。";
    if (code === "timeout") return "上傳逾時，可能網路太慢或檔案太大，請改用 PNG 壓縮再試。";
    if (status === 413 || code === "file_too_large_10mb") return "圖片太大（超過 10 MB），請先壓縮再上傳。";
    if (status === 415 || code === "unsupported_content_type") return "目前只支援 PNG / JPG / PDF 三種格式。";
    if (status === 400 && code === "missing_image") return "沒有偵測到上傳的圖檔，請重新選一張。";
    if (status === 400 && code === "empty_image") return "圖檔內容是空的，請改用其他檔案。";
    if (status === 400 && code === "floor_not_found") return "找不到這層樓的資料，可能已被刪除，請回上一頁重新整理。";
    if (status === 401) return "登入狀態已逾時，請重新登入後再試。";
    if (status === 403) return "您目前的角色不能編輯這層樓的圖資。";
    if (status === 429) return "AI 服務目前流量過大，請等 1 分鐘後再重新解析。";
    // Soft failures returned in 502/503 with a body
    if (code === "openai_unconfigured") return "尚未設定 OpenAI 金鑰；請至「平台金鑰」頁面填入後再試。";
    if (code === "openai_timeout") return "AI 解析超時（90 秒未完成），通常是圖片太大或太複雜，請改用較小的圖再試。";
    if (code === "openai_call_failed") return "AI 服務暫時無法連線，請過幾分鐘再試。";
    if (code === "openai_invalid_shape") return "AI 解析結果格式不符（可能解析度太低）；請改用更清楚的圖檔或人工編輯。";
    if (code === "layouts_bucket_unbound") return "後端儲存空間尚未配置完成，請通知系統管理員。";
  }
  return stage === "upload"
    ? "圖片上傳失敗，請稍後再試或改用人工編輯。"
    : "AI 解析暫時無法完成，請稍後再試或改用人工編輯。";
}

// Two flavors of "select": click-to-pick a single entity (legacy, "V")
// and marquee-drag to pick everything inside a rectangle ("M"). Both
// drive the same per-entity highlight + selection state, but the
// canvas pointer logic differs (single click vs. mousedown→drag→up).
type EditorTool = "select" | "select_marquee" | "wall" | "room" | "door" | "delete";

/** Bounding rectangle of an in-progress marquee drag, in image-space px. */
interface MarqueeRect { x1: number; y1: number; x2: number; y2: number }
type LayerKey = "walls" | "rooms" | "doors" | "corridors" | "labels" | "entry_points";

interface SelectionId {
  kind: "wall" | "room" | "door" | "corridor" | "entry_point" | "label";
  id: string;
  vertexIndex?: number;
}

interface State {
  layout: NormalizedLayoutDto;
  layoutId: string | null;
  versionId: string | null;
  /**
   * Pipeline phase:
   *   idle      — no upload in flight
   *   uploading — XHR is still pushing bytes (uploadPercent meaningful)
   *   parsing   — bytes received, worker is calling OpenAI (elapsed timer)
   *   ready     — parse complete, layout rendered
   *   error     — failed (parseError carries zh-TW copy)
   */
  parseStatus: "idle" | "uploading" | "parsing" | "ready" | "error";
  parseError: string | null;
  parseCostUsd: number | null;
  /** 0..1 byte-transfer ratio while parseStatus === "uploading". */
  uploadPercent: number;
  uploadBytesLoaded: number;
  uploadBytesTotal: number;
  /** ms epoch when we entered the "parsing" (AI thinking) phase. */
  parsingStartedAt: number | null;
  history: NormalizedLayoutDto[];
  historyIndex: number;
  selection: SelectionId | null;
  /** Multi-select set produced by the marquee tool. Null when not in
   *  marquee mode; empty array when in mode but nothing selected yet. */
  marqueeSelection: SelectionId[] | null;
  /** In-progress marquee drag rectangle. Null when not currently
   *  dragging. */
  marquee: MarqueeRect | null;
  tool: EditorTool;
  // Active drawing state — temp points the user has clicked.
  drafting: LayoutPoint[] | null;
  layers: Record<LayerKey, boolean>;
  zoom: number;
  pan: { x: number; y: number };
  dirty: boolean;
}

type Action =
  | { type: "loaded"; layout: NormalizedLayoutDto; layoutId: string; versionId: string }
  | { type: "upload_start" }
  | { type: "upload_progress"; percent: number; loaded: number; total: number }
  | { type: "ai_parsing_start" }
  | { type: "parse_done"; layout: NormalizedLayoutDto; layoutId: string; versionId: string; costUsd: number }
  | { type: "parse_failed"; message: string }
  | { type: "set_layout"; layout: NormalizedLayoutDto; pushHistory?: boolean }
  | { type: "select"; sel: SelectionId | null }
  | { type: "marquee_start"; pt: LayoutPoint }
  | { type: "marquee_update"; pt: LayoutPoint }
  | { type: "marquee_end"; selection: SelectionId[] }
  | { type: "marquee_clear" }
  | { type: "tool"; tool: EditorTool }
  | { type: "drafting_add"; pt: LayoutPoint }
  | { type: "drafting_clear" }
  | { type: "toggle_layer"; layer: LayerKey }
  | { type: "zoom"; factor: number }
  | { type: "pan"; dx: number; dy: number }
  | { type: "reset_view" }
  | { type: "undo" }
  | { type: "redo" }
  | { type: "saved" }
  | { type: "reset" };

function init(): State {
  return {
    layout: EMPTY_LAYOUT,
    layoutId: null,
    versionId: null,
    parseStatus: "idle",
    parseError: null,
    parseCostUsd: null,
    uploadPercent: 0,
    uploadBytesLoaded: 0,
    uploadBytesTotal: 0,
    parsingStartedAt: null,
    history: [EMPTY_LAYOUT],
    historyIndex: 0,
    selection: null,
    marqueeSelection: null,
    marquee: null,
    tool: "select",
    drafting: null,
    layers: {
      walls: true, rooms: true, doors: true,
      corridors: true, labels: true, entry_points: true,
    },
    zoom: 1,
    pan: { x: 0, y: 0 },
    dirty: false,
  };
}

function pushHistory(state: State, next: NormalizedLayoutDto): State {
  const trimmed = state.history.slice(0, state.historyIndex + 1);
  trimmed.push(next);
  // Cap at HISTORY_DEPTH steps + the initial state.
  const overflow = trimmed.length - (HISTORY_DEPTH + 1);
  const sliced = overflow > 0 ? trimmed.slice(overflow) : trimmed;
  return {
    ...state,
    layout: next,
    history: sliced,
    historyIndex: sliced.length - 1,
    dirty: true,
  };
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "loaded":
      return {
        ...state,
        layout: action.layout,
        layoutId: action.layoutId,
        versionId: action.versionId,
        history: [action.layout],
        historyIndex: 0,
        parseStatus: "ready",
        parseError: null,
        dirty: false,
      };
    case "upload_start":
      return {
        ...state,
        parseStatus: "uploading",
        parseError: null,
        uploadPercent: 0,
        uploadBytesLoaded: 0,
        uploadBytesTotal: 0,
        parsingStartedAt: null,
      };
    case "upload_progress":
      return {
        ...state,
        uploadPercent: action.percent,
        uploadBytesLoaded: action.loaded,
        uploadBytesTotal: action.total,
      };
    case "ai_parsing_start":
      return {
        ...state,
        parseStatus: "parsing",
        uploadPercent: 1,
        parsingStartedAt: Date.now(),
      };
    case "parse_done":
      return {
        ...state,
        layout: action.layout,
        layoutId: action.layoutId,
        versionId: action.versionId,
        parseStatus: "ready",
        parseError: null,
        parseCostUsd: action.costUsd,
        history: [action.layout],
        historyIndex: 0,
        dirty: false,
      };
    case "parse_failed":
      return { ...state, parseStatus: "error", parseError: action.message };
    case "set_layout":
      return action.pushHistory === false
        ? { ...state, layout: action.layout }
        : pushHistory(state, action.layout);
    case "select":
      return { ...state, selection: action.sel, marqueeSelection: null, marquee: null };
    case "marquee_start":
      return {
        ...state,
        marquee: { x1: action.pt.x, y1: action.pt.y, x2: action.pt.x, y2: action.pt.y },
        marqueeSelection: [],
        selection: null,
      };
    case "marquee_update":
      if (!state.marquee) return state;
      return { ...state, marquee: { ...state.marquee, x2: action.pt.x, y2: action.pt.y } };
    case "marquee_end":
      return { ...state, marquee: null, marqueeSelection: action.selection, selection: null };
    case "marquee_clear":
      return { ...state, marquee: null, marqueeSelection: null };
    case "tool":
      // Switching tool wipes everything: drafting, single selection,
      // marquee selection, and any in-flight marquee drag rectangle.
      return {
        ...state,
        tool: action.tool,
        drafting: null,
        selection: null,
        marqueeSelection: null,
        marquee: null,
      };
    case "drafting_add":
      return { ...state, drafting: [...(state.drafting ?? []), action.pt] };
    case "drafting_clear":
      return { ...state, drafting: null };
    case "toggle_layer":
      return { ...state, layers: { ...state.layers, [action.layer]: !state.layers[action.layer] } };
    case "zoom":
      return { ...state, zoom: Math.max(0.2, Math.min(8, state.zoom * action.factor)) };
    case "pan":
      return { ...state, pan: { x: state.pan.x + action.dx, y: state.pan.y + action.dy } };
    case "reset_view":
      return { ...state, zoom: 1, pan: { x: 0, y: 0 } };
    case "undo": {
      if (state.historyIndex <= 0) return state;
      const i = state.historyIndex - 1;
      return { ...state, historyIndex: i, layout: state.history[i]!, dirty: true, selection: null };
    }
    case "redo": {
      if (state.historyIndex >= state.history.length - 1) return state;
      const i = state.historyIndex + 1;
      return { ...state, historyIndex: i, layout: state.history[i]!, dirty: true, selection: null };
    }
    case "saved":
      return { ...state, dirty: false };
    case "reset":
      // Wipe to fresh-empty: drops layoutId/versionId so the next load
      // hits the DropZone path. Keep `tool` etc. so the operator's
      // pen/zoom/layer toggles aren't reset by the action.
      return {
        ...init(),
        tool: state.tool,
        layers: state.layers,
        zoom: state.zoom,
        pan: state.pan,
      };
  }
}

// ── Component ────────────────────────────────────────────────────────────

export default function FloorLayoutEditor(): React.ReactElement {
  const { siteId, buildingId, floorId } = useParams();
  const navigate = useNavigate();
  const session = useDashboardSession();
  const tenantId = session.acting_tenant_id ?? session.tenant_id ?? null;
  const dlg = useConfirmDialog();

  const [state, dispatch] = useReducer(reducer, undefined, init);
  const [saving, setSaving] = useState(false);
  const [reparsing, setReparsing] = useState(false);
  const [toast, setToast] = useState<{ kind: "ok" | "err"; msg: string } | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  // Location markers (the user-facing "地點 / Tag" overlay).
  // Source of truth lives in D1 (`map_markers`); the local copy is
  // updated optimistically during drag and re-synced after every
  // patchMarker / createMarker / archiveMarker call so a refresh shows
  // exactly what the canvas does.
  const [markers, setMarkers] = useState<MapMarkerDto[]>([]);
  const [siteLocations, setSiteLocations] = useState<LocationRow[]>([]);
  const [markerPanelOpen, setMarkerPanelOpen] = useState(false);
  // When the user clicks "點此放置" on an unplaced location we enter
  // a placement mode: the next canvas click creates a marker linked to
  // this location at that position. ESC cancels.
  const [placingLocationId, setPlacingLocationId] = useState<string | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  // Spacebar pan tracking — held + dragging on canvas.
  const [spacePanning, setSpacePanning] = useState(false);
  /** Active mouse-pan gesture. Non-null while the operator holds
   *  Spacebar (or middle-mouse-button) and drags — mousemove walks
   *  state.pan by the delta between successive client positions, mouseup
   *  clears the ref. Touch path uses panRef instead. */
  const mousePanRef = useRef<{ lastX: number; lastY: number } | null>(null);

  // Canvas view mode — "2d" is the editable SVG canvas (default), "3d"
  // mounts the lazy-loaded three.js viewer with marker drag enabled but
  // wall / room / door geometry edits disabled. Toggle via the toolbar
  // 「3D」button. iOS WebGL context-loss falls back to 2D automatically.
  const [viewMode, setViewMode] = useState<"2d" | "3d">("2d");
  // Distinguishes operator-initiated close (clicking "切回 2D") from a
  // genuine WebGL context loss. Without this, r3f's Canvas dispose path
  // calls forceContextLoss() during unmount → fires our
  // webglcontextlost listener → onContextLost() shows the misleading
  // toast even though the user just intentionally clicked back.
  const userClosed3dRef = useRef(false);
  const closeTo2D = useCallback(() => {
    userClosed3dRef.current = true;
    setViewMode("2d");
  }, []);

  // Global mouseup handler — covers the case where the operator
  // releases outside the SVG (cursor wandered to the toolbar / sidebar
  // mid-pan). Without this, mousePanRef leaks and the next click
  // continues panning.
  useEffect(() => {
    const onUp = (): void => { mousePanRef.current = null; };
    window.addEventListener("mouseup", onUp);
    return () => window.removeEventListener("mouseup", onUp);
  }, []);
  const locationLookup = useMemo(() => {
    const m = new Map<string, { code: string; label: string }>();
    for (const c of siteLocations) m.set(c.id, { code: c.code, label: c.label });
    return m;
  }, [siteLocations]);
  const onMarkerMove3D = useCallback(async (markerId: string, position: { x: number; y: number }) => {
    // Optimistic update + persist (mirrors the 2D drag handler).
    setMarkers((prev) => prev.map((mm) =>
      mm.id === markerId ? { ...mm, position_x: position.x, position_y: position.y } : mm,
    ));
    try {
      await patchMarker(markerId, { position_x: position.x, position_y: position.y }, tenantId);
    } catch (e) {
      setToast({ kind: "err", msg: friendlyFloorStackError(e) });
    }
  }, [tenantId]);

  // ── Load existing layout on mount ──
  useEffect(() => {
    if (!floorId) return;
    let cancelled = false;
    void getFloorLayout(floorId, tenantId)
      .then(async (data) => {
        if (cancelled || !data || !data.current_version) return;
        try {
          const parsed = JSON.parse(data.current_version.normalized_json) as NormalizedLayoutDto;
          if (parsed && parsed.image_size) {
            dispatch({
              type: "loaded",
              layout: { ...EMPTY_LAYOUT, ...parsed },
              layoutId: data.layout!.id,
              versionId: data.current_version.id,
            });
          }
        } catch { /* leave empty */ }
        // After we know which version is current, fetch its markers.
        try {
          const items = await listMarkers(data.layout!.id, data.current_version!.id, tenantId);
          if (!cancelled) setMarkers(items);
        } catch { /* keep empty */ }
      })
      .catch(() => { /* show empty editor */ });
    return () => { cancelled = true; };
  }, [floorId, tenantId]);

  // ── Load all locations for this site (used by the markers panel) ──
  // We list at the site level rather than per-floor because the
  // /v1/locations endpoint doesn't yet expose floor_id as a filter;
  // the panel filters in JS to "already placed on this floor" vs.
  // "available to place". Sites typically have <100 locations so the
  // overhead is negligible.
  useEffect(() => {
    if (!siteId) return;
    let cancelled = false;
    void listLocations({ siteId, tenantId, limit: 500 })
      .then((r) => {
        if (cancelled) return;
        setSiteLocations(r.items.filter((c) => c.status === "active"));
      })
      .catch(() => { /* keep empty list */ });
    return () => { cancelled = true; };
  }, [siteId, tenantId]);

  // After parse_done lands, refetch markers since the layout version id
  // changed (the parse handler now promotes the new version to current,
  // and any markers from the previous version are tied to that older
  // version — they won't show up against the new versionId).
  useEffect(() => {
    if (state.parseStatus !== "ready" || !state.layoutId || !state.versionId) return;
    let cancelled = false;
    void listMarkers(state.layoutId, state.versionId, tenantId)
      .then((items) => { if (!cancelled) setMarkers(items); })
      .catch(() => { /* keep prior list */ });
    return () => { cancelled = true; };
  }, [state.parseStatus, state.layoutId, state.versionId, tenantId]);

  // ── Keyboard shortcuts ──
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.code === "Space" && !e.repeat) { e.preventDefault(); setSpacePanning(true); return; }
      const mod = e.metaKey || e.ctrlKey;
      if (mod && e.key === "z" && !e.shiftKey) { e.preventDefault(); dispatch({ type: "undo" }); return; }
      if (mod && (e.key === "y" || (e.key === "z" && e.shiftKey))) { e.preventDefault(); dispatch({ type: "redo" }); return; }
      if (e.key === "Delete" || e.key === "Backspace") {
        if (state.marqueeSelection && state.marqueeSelection.length > 0) {
          e.preventDefault();
          deleteMarqueeSelection();
          return;
        }
        if (state.selection) { e.preventDefault(); deleteSelection(); }
        return;
      }
      // Arrow-key nudge — 1 px without modifier, 10 px with Shift.
      // Skip when Cmd/Ctrl is held (those are word/line navigation
      // shortcuts the OS / browser owns and we shouldn't steal).
      if (!mod && (e.key === "ArrowUp" || e.key === "ArrowDown" || e.key === "ArrowLeft" || e.key === "ArrowRight")) {
        if (!state.selection && !(state.marqueeSelection && state.marqueeSelection.length > 0)) return;
        const step = e.shiftKey ? 10 : 1;
        const dx = e.key === "ArrowLeft" ? -step : e.key === "ArrowRight" ? step : 0;
        const dy = e.key === "ArrowUp"   ? -step : e.key === "ArrowDown"  ? step : 0;
        e.preventDefault();
        nudgeSelection(dx, dy);
        return;
      }
      if (e.key === "v") dispatch({ type: "tool", tool: "select" });
      if (e.key === "m") dispatch({ type: "tool", tool: "select_marquee" });
      if (e.key === "w") dispatch({ type: "tool", tool: "wall" });
      if (e.key === "r") dispatch({ type: "tool", tool: "room" });
      if (e.key === "d") dispatch({ type: "tool", tool: "door" });
      if (e.key === "Escape") {
        dispatch({ type: "drafting_clear" });
        dispatch({ type: "select", sel: null });
        // ESC also cancels any pending location placement.
        if (placingLocationId) setPlacingLocationId(null);
      }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code === "Space") setSpacePanning(false);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("keyup", onKeyUp);
    return () => { window.removeEventListener("keydown", onKey); window.removeEventListener("keyup", onKeyUp); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.selection, state.marqueeSelection, state.layout]);

  const onPickFile = () => fileRef.current?.click();

  // Upload flow now hands the file to the async map-import pipeline
  // (POST /v1/map-imports → queue → progress page). The synchronous
  // /v1/floors/:id/layout-parse path is gone — every parse goes through
  // the staged pipeline so the user gets honest progress, error retry,
  // and approve-to-publish, instead of staring at a single spinner for
  // 30-60s with no way to cancel.
  //
  // The browser uploads bytes here; the moment the gateway has the file
  // we redirect to /dashboard/map-imports/:id. The progress page polls
  // the job row and surfaces every state transition; once it reaches
  // `ready_for_review` the operator approves and the canonical
  // map_layout_versions row is written, after which a reload of this
  // editor loads the published layout via the normal getFloorLayout
  // path.
  const onDropFile = useCallback(async (file: File) => {
    if (!floorId) return;
    if (file.size > 25 * 1024 * 1024) {
      setToast({ kind: "err", msg: "圖片太大（超過 25 MB），請先壓縮再上傳。" });
      return;
    }
    const ok = ["image/png", "image/jpeg", "image/jpg", "image/webp", "application/pdf"].includes(file.type);
    if (!ok) {
      setToast({ kind: "err", msg: "目前只支援 PNG / JPG / WebP / PDF 四種格式。" });
      return;
    }
    dispatch({ type: "upload_start" });
    setToast(null);
    try {
      const r = await createMapImport({
        file,
        site_id: siteId ?? null,
        building_id: buildingId ?? null,
        floor_id: floorId,
        tenant_id: tenantId,
        onProgress: (p) => {
          dispatch({
            type: "upload_progress",
            percent: p.bytesPercent,
            loaded: p.bytesLoaded,
            total: p.bytesTotal,
          });
        },
      });
      // Hand off to the staged-pipeline progress page; everything from
      // queue → OpenAI → SVG → scene-graph → approve runs there.
      navigate(`/dashboard/map-imports/${encodeURIComponent(r.job.id)}`);
    } catch (e) {
      const msg = friendlyCreateImportError(e);
      dispatch({ type: "parse_failed", message: msg });
      setToast({ kind: "err", msg });
    }
  }, [floorId, siteId, buildingId, tenantId, navigate]);

  const onReparse = async () => {
    if (!floorId || !state.versionId) return;
    setReparsing(true); setToast(null);
    dispatch({ type: "ai_parsing_start" });
    try {
      const r = await reparseLayoutVersion(floorId, state.versionId, tenantId);
      if (r.ok) {
        dispatch({ type: "parse_done", layout: { ...EMPTY_LAYOUT, ...r.normalized }, layoutId: r.layout_id, versionId: r.version_id, costUsd: r.parse_cost_usd });
        setToast({
          kind: "ok",
          msg: `已重新解析 ✓ 偵測到 ${r.normalized.walls.length} 牆 / ${r.normalized.rooms.length} 房（成本約 $${r.parse_cost_usd.toFixed(4)}）。`,
        });
      } else {
        const msg = r.message ?? friendlyParseStageError("ai", { code: r.code, status: 503 });
        dispatch({ type: "parse_failed", message: msg });
        setToast({ kind: "err", msg });
      }
    } catch (e) {
      const msg = friendlyParseStageError("ai", e);
      dispatch({ type: "parse_failed", message: msg });
      setToast({ kind: "err", msg });
    }
    finally { setReparsing(false); }
  };

  const [clearing, setClearing] = useState(false);
  // 「清除底圖」flow: confirm → DELETE /v1/floors/:id/layout → reset
  // local state to empty so the DropZone re-appears for a fresh upload.
  // Markers stay in D1 attached to the (now-archived) old version; the
  // next successful parse migrates them to the new current version
  // (PR #137 carry-over). We also clear the local `markers` cache so
  // the operator doesn't see stale pins floating over the empty canvas.
  const onClearBaseMap = useCallback(async () => {
    if (!floorId) return;
    if (!(await dlg.confirm("確認清除這層樓的底圖？\n\n會把目前所有版本封存（草稿 / 已發佈都會），地圖會回到空白狀態，可重新上傳新底圖。\n\n已放置的地點標記不會被刪除，下次成功解析新底圖後會自動接到新版本上。", { destructive: true, okLabel: "清除底圖" }))) return;
    setClearing(true);
    setToast(null);
    try {
      await clearFloorLayout(floorId, tenantId);
      dispatch({ type: "reset" });
      setMarkers([]);
      setToast({ kind: "ok", msg: "已清除底圖。可重新上傳一張新的平面圖。" });
    } catch (e) {
      setToast({ kind: "err", msg: friendlyFloorStackError(e) });
    } finally {
      setClearing(false);
    }
  }, [floorId, tenantId]);

  const onSave = async () => {
    if (!state.layoutId || !state.versionId) {
      setToast({ kind: "err", msg: "尚未上傳樓層圖，請先上傳或解析後再儲存。" });
      return;
    }
    setSaving(true); setToast(null);
    try {
      await saveLayoutEdits(state.layoutId, state.versionId, state.layout, tenantId);
      dispatch({ type: "saved" });
      setToast({ kind: "ok", msg: "已儲存編輯結果。" });
    } catch (e) { setToast({ kind: "err", msg: friendlyFloorStackError(e) }); }
    finally { setSaving(false); }
  };

  const onExportSvg = () => downloadSvg(state.layout, "layout.svg");
  const onExportJson = () =>
    downloadBlob(new Blob([JSON.stringify(state.layout, null, 2)], { type: "application/json" }), "layout.json");

  // ── Geometry edits ──
  const replaceWall = (id: string, patch: Partial<LayoutWall>) => {
    const next: NormalizedLayoutDto = {
      ...state.layout,
      walls: state.layout.walls.map((w) => (w.id === id ? { ...w, ...patch } : w)),
    };
    dispatch({ type: "set_layout", layout: next });
  };

  const replaceRoomVertex = (id: string, idx: number, pt: LayoutPoint) => {
    const next: NormalizedLayoutDto = {
      ...state.layout,
      rooms: state.layout.rooms.map((r) =>
        r.id === id ? { ...r, vertices: r.vertices.map((v, i) => (i === idx ? pt : v)) } : r,
      ),
    };
    dispatch({ type: "set_layout", layout: next });
  };

  const replaceDoor = (id: string, patch: Partial<LayoutDoor>) => {
    const next: NormalizedLayoutDto = {
      ...state.layout,
      doors: state.layout.doors.map((d) => (d.id === id ? { ...d, ...patch } : d)),
    };
    dispatch({ type: "set_layout", layout: next });
  };

  const deleteSelection = () => {
    if (!state.selection) return;
    const sel = state.selection;
    const layout = state.layout;
    let next: NormalizedLayoutDto = layout;
    switch (sel.kind) {
      case "wall":
        next = { ...layout, walls: layout.walls.filter((w) => w.id !== sel.id) }; break;
      case "room":
        next = { ...layout, rooms: layout.rooms.filter((r) => r.id !== sel.id) }; break;
      case "door":
        next = { ...layout, doors: layout.doors.filter((d) => d.id !== sel.id) }; break;
      case "corridor":
        next = { ...layout, corridors: layout.corridors.filter((c) => c.id !== sel.id) }; break;
      case "entry_point":
        next = { ...layout, entry_points: layout.entry_points.filter((e) => e.id !== sel.id) }; break;
      case "label":
        next = { ...layout, labels: layout.labels.filter((l) => l.id !== sel.id) }; break;
    }
    dispatch({ type: "set_layout", layout: next });
    dispatch({ type: "select", sel: null });
  };

  /** Bulk-delete every entity in the marquee selection. Done as a
   *  single set_layout dispatch so undo lifts the entire group at once
   *  instead of one entity at a time. */
  const deleteMarqueeSelection = () => {
    if (!state.marqueeSelection || state.marqueeSelection.length === 0) return;
    const ids = {
      wall: new Set<string>(), room: new Set<string>(), door: new Set<string>(),
      corridor: new Set<string>(), entry_point: new Set<string>(), label: new Set<string>(),
    };
    for (const s of state.marqueeSelection) {
      ids[s.kind].add(s.id);
    }
    const layout = state.layout;
    const next: NormalizedLayoutDto = {
      ...layout,
      walls: layout.walls.filter((w) => !ids.wall.has(w.id)),
      rooms: layout.rooms.filter((r) => !ids.room.has(r.id)),
      doors: layout.doors.filter((d) => !ids.door.has(d.id)),
      corridors: layout.corridors.filter((c) => !ids.corridor.has(c.id)),
      entry_points: layout.entry_points.filter((e) => !ids.entry_point.has(e.id)),
      labels: layout.labels.filter((l) => !ids.label.has(l.id)),
    };
    dispatch({ type: "set_layout", layout: next });
    dispatch({ type: "marquee_clear" });
  };

  /**
   * Arrow-key nudge. Translates every point on the selected entities by
   * (dx, dy) in image-space pixels.
   *
   * Door's `swing_to` is a UNIT DIRECTION vector (not an absolute
   * coordinate), so we deliberately move only the `hinge` — translating
   * swing_to would rotate the door instead of moving it. Walls / rooms
   * / corridors get every endpoint shifted; entry_point / label have a
   * single `point` to update.
   *
   * History grouping: consecutive arrow presses inside the same "nudge
   * session" (≤ 500ms between presses) collapse into ONE history entry
   * so undo doesn't have to step through every 1-px movement when the
   * user holds the arrow. The first press of a fresh session is the
   * one that pushes onto history.
   */
  const nudgeSessionRef = useRef<number | null>(null);
  const translate = (p: LayoutPoint, dx: number, dy: number): LayoutPoint => ({ x: p.x + dx, y: p.y + dy });
  const applyNudge = (layout: NormalizedLayoutDto, ids: {
    wall: Set<string>; room: Set<string>; door: Set<string>;
    corridor: Set<string>; entry_point: Set<string>; label: Set<string>;
  }, dx: number, dy: number): NormalizedLayoutDto => ({
    ...layout,
    walls: layout.walls.map((w) => ids.wall.has(w.id)
      ? { ...w, points: [translate(w.points[0], dx, dy), translate(w.points[1], dx, dy)] }
      : w),
    rooms: layout.rooms.map((r) => ids.room.has(r.id)
      ? { ...r, vertices: r.vertices.map((v) => translate(v, dx, dy)) }
      : r),
    doors: layout.doors.map((d) => ids.door.has(d.id)
      ? { ...d, hinge: translate(d.hinge, dx, dy) }
      : d),
    corridors: layout.corridors.map((c) => ids.corridor.has(c.id)
      ? { ...c, centerline: c.centerline.map((p) => translate(p, dx, dy)) }
      : c),
    entry_points: layout.entry_points.map((e) => ids.entry_point.has(e.id)
      ? { ...e, point: translate(e.point, dx, dy) }
      : e),
    labels: layout.labels.map((l) => ids.label.has(l.id)
      ? { ...l, point: translate(l.point, dx, dy) }
      : l),
  });
  const nudgeSelection = (dx: number, dy: number) => {
    const ids = {
      wall: new Set<string>(), room: new Set<string>(), door: new Set<string>(),
      corridor: new Set<string>(), entry_point: new Set<string>(), label: new Set<string>(),
    };
    if (state.marqueeSelection && state.marqueeSelection.length > 0) {
      for (const s of state.marqueeSelection) ids[s.kind].add(s.id);
    } else if (state.selection) {
      ids[state.selection.kind].add(state.selection.id);
    } else {
      return;
    }
    const now = Date.now();
    const inSession = nudgeSessionRef.current !== null && (now - nudgeSessionRef.current) < 500;
    nudgeSessionRef.current = now;
    dispatch({ type: "set_layout", layout: applyNudge(state.layout, ids, dx, dy), pushHistory: !inSession });
  };

  // ── Canvas → image-space coordinate conversion ──
  // We deliberately don't use `svg.getScreenCTM()` here. The SVG carries
  // a CSS `transform: scale(zoom) translate(panX, panY)` (toolbar zoom +
  // touch-pan), and iOS WebKit's getScreenCTM does NOT compose that CSS
  // transform into its returned matrix — so the inverse mapping was
  // off-by-zoom after the user pinch-zoomed and dragging a marker
  // landed it at the wrong image-space coordinate.
  //
  // `getBoundingClientRect()` returns the final on-screen rect with
  // every CSS transform applied, in every browser. Combined with the
  // viewBox values + an explicit fit calculation for
  // `preserveAspectRatio="xMidYMid meet"` (which letterboxes when the
  // viewBox aspect ratio doesn't match the rendered rect), the math is
  // independent of any CSS transform applied above the SVG.
  const toImageSpace = (clientX: number, clientY: number): LayoutPoint => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const rect = svg.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return { x: 0, y: 0 };
    const vb = svg.viewBox.baseVal;
    if (!vb || vb.width <= 0 || vb.height <= 0) return { x: 0, y: 0 };
    const vbAspect = vb.width / vb.height;
    const rectAspect = rect.width / rect.height;
    let displayedW: number;
    let displayedH: number;
    let offsetX: number;
    let offsetY: number;
    if (vbAspect > rectAspect) {
      // viewBox wider than rect → fit to width, letterbox top/bottom
      displayedW = rect.width;
      displayedH = rect.width / vbAspect;
      offsetX = 0;
      offsetY = (rect.height - displayedH) / 2;
    } else {
      // viewBox taller than rect → fit to height, letterbox left/right
      displayedH = rect.height;
      displayedW = rect.height * vbAspect;
      offsetX = (rect.width - displayedW) / 2;
      offsetY = 0;
    }
    const localX = vb.x + ((clientX - rect.left - offsetX) / displayedW) * vb.width;
    const localY = vb.y + ((clientY - rect.top - offsetY) / displayedH) * vb.height;
    return { x: Math.round(localX), y: Math.round(localY) };
  };

  // ── Location marker actions ────────────────────────────────────────
  const placeLocationAt = useCallback(async (locationId: string, pt: LayoutPoint) => {
    if (!state.layoutId || !state.versionId) return;
    const cp = siteLocations.find((c) => c.id === locationId);
    if (!cp) return;
    try {
      const r = await createMarker(
        state.layoutId,
        state.versionId,
        {
          marker_type: "location",
          position_x: pt.x,
          position_y: pt.y,
          linked_location_id: locationId,
          label: cp.code,
        },
        tenantId,
      );
      // Optimistic insert — backend won't have given us the full row,
      // so build a minimum viable one. Next list refresh will reconcile.
      setMarkers((prev) => [
        ...prev,
        {
          id: r.id,
          tenant_id: cp.tenant_id,
          layout_id: state.layoutId!,
          layout_version_id: state.versionId!,
          floor_id: floorId ?? "",
          marker_type: "location",
          linked_location_id: locationId,
          position_x: pt.x,
          position_y: pt.y,
          rotation_deg: 0,
          label: cp.code,
          payload_json: "{}",
          status: "active",
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
      ]);
      setPlacingLocationId(null);
      setToast({ kind: "ok", msg: `已放置 ${cp.code}（${cp.label}）。可繼續拖曳調整位置。` });
    } catch (e) {
      setToast({ kind: "err", msg: friendlyFloorStackError(e) });
    }
  }, [state.layoutId, state.versionId, floorId, siteLocations, tenantId]);

  const removeLocationMarker = useCallback(async (markerId: string) => {
    try {
      await archiveMarker(markerId, tenantId);
      setMarkers((prev) => prev.filter((m) => m.id !== markerId));
      setToast({ kind: "ok", msg: "已從地圖移除地點。" });
    } catch (e) {
      setToast({ kind: "err", msg: friendlyFloorStackError(e) });
    }
  }, [tenantId]);

  // Drag state for location markers. While dragging we update the
  // local copy so the user sees the marker follow the cursor; the DB
  // write happens once on mouseup so we don't hammer patchMarker.
  const markerDragRef = useRef<{ id: string; startX: number; startY: number } | null>(null);
  const onMarkerMouseDown = (markerId: string) => (e: React.MouseEvent) => {
    e.stopPropagation();
    if (state.tool !== "select") {
      // Even outside select mode, marker drag should always work — it's
      // the primary interaction the user asked for ("拖曳移動地點位置").
    }
    const m = markers.find((mm) => mm.id === markerId);
    if (!m) return;
    markerDragRef.current = { id: markerId, startX: m.position_x, startY: m.position_y };
  };
  const onMarkerMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!markerDragRef.current) return;
    // Snapshot the id BEFORE calling setMarkers — React runs the
    // updater callback lazily, and on touch devices `pointerend` may
    // fire (and clear `markerDragRef.current`) between the time we
    // schedule the update and the time React actually executes it,
    // which made `markerDragRef.current!.id` inside the closure throw
    // `null is not an object (evaluating 'ge.current.id')`.
    const dragId = markerDragRef.current.id;
    const pt = toImageSpace(e.clientX, e.clientY);
    setMarkers((prev) => prev.map((m) =>
      m.id === dragId ? { ...m, position_x: pt.x, position_y: pt.y } : m,
    ));
  };
  const onMarkerMouseUp = async () => {
    const drag = markerDragRef.current;
    markerDragRef.current = null;
    if (!drag) return;
    const m = markers.find((mm) => mm.id === drag.id);
    if (!m) return;
    if (m.position_x === drag.startX && m.position_y === drag.startY) return; // no-op click
    try {
      await patchMarker(m.id, { position_x: m.position_x, position_y: m.position_y }, tenantId);
    } catch (e) {
      // Roll back optimistic update on failure.
      setMarkers((prev) => prev.map((mm) =>
        mm.id === drag.id ? { ...mm, position_x: drag.startX, position_y: drag.startY } : mm,
      ));
      setToast({ kind: "err", msg: friendlyFloorStackError(e) });
    }
  };

  // ── Canvas pointer logic ──
  const onCanvasMouseDown = (e: React.MouseEvent<SVGSVGElement>) => {
    // Pan modes — Spacebar held OR middle-mouse-button. The spacebar
    // path also fires on entity targets (so the operator can pan even
    // while the cursor sits over a wall vertex), middle-button pan is
    // canvas-only.
    if (spacePanning || e.button === 1) {
      e.preventDefault();
      mousePanRef.current = { lastX: e.clientX, lastY: e.clientY };
      return;
    }
    const target = e.target as SVGElement;
    if (target.dataset.entityKind) return; // entity handlers will fire
    const pt = toImageSpace(e.clientX, e.clientY);
    // Placement mode for a location marker: the next canvas click
    // creates the marker and exits placement mode. Has priority over
    // every other tool so an accidental "wall" click doesn't fire.
    if (placingLocationId && state.layoutId && state.versionId) {
      void placeLocationAt(placingLocationId, pt);
      return;
    }
    if (state.tool === "wall") {
      if (!state.drafting || state.drafting.length === 0) { dispatch({ type: "drafting_add", pt }); return; }
      // Second click → finalize wall.
      const start = state.drafting[0]!;
      const wall: LayoutWall = {
        id: `wall_${state.layout.walls.length + 1}_${Date.now().toString(36)}`,
        kind: "interior",
        points: [start, pt],
        thickness_px: 4,
      };
      dispatch({ type: "set_layout", layout: { ...state.layout, walls: [...state.layout.walls, wall] } });
      dispatch({ type: "drafting_clear" });
    } else if (state.tool === "room") {
      // Click adds a vertex; double-click closes (handled below).
      dispatch({ type: "drafting_add", pt });
    } else if (state.tool === "door") {
      if (!state.drafting || state.drafting.length === 0) { dispatch({ type: "drafting_add", pt }); return; }
      const hinge = state.drafting[0]!;
      const door: LayoutDoor = {
        id: `door_${state.layout.doors.length + 1}_${Date.now().toString(36)}`,
        hinge, swing_to: pt, label: null,
      };
      dispatch({ type: "set_layout", layout: { ...state.layout, doors: [...state.layout.doors, door] } });
      dispatch({ type: "drafting_clear" });
    } else if (state.tool === "select") {
      dispatch({ type: "select", sel: null });
    } else if (state.tool === "select_marquee") {
      // Begin marquee drag. mousemove updates the rect, mouseup
      // commits the selection. The canvas owns the entire drag —
      // entity handlers no-op while the marquee is in flight.
      dispatch({ type: "marquee_start", pt });
    }
  };

  const onCanvasDoubleClick = (e: React.MouseEvent<SVGSVGElement>) => {
    if (state.tool !== "room" || !state.drafting || state.drafting.length < 3) return;
    e.preventDefault();
    const room: LayoutRoom = {
      id: `room_${state.layout.rooms.length + 1}_${Date.now().toString(36)}`,
      vertices: state.drafting,
      label: null,
      kind: "unknown",
    };
    dispatch({ type: "set_layout", layout: { ...state.layout, rooms: [...state.layout.rooms, room] } });
    dispatch({ type: "drafting_clear" });
  };

  const onCanvasWheel = (e: React.WheelEvent<SVGSVGElement>) => {
    e.preventDefault();
    const factor = e.deltaY < 0 ? 1.1 : 1 / 1.1;
    dispatch({ type: "zoom", factor });
  };

  // ── Touch gestures (mobile / tablet) ──
  // Single-finger drag on blank canvas → pan. Two-finger drag → pinch
  // zoom. Tap on an entity (wall vertex / room vertex / door / marker)
  // still falls through to the existing mouse-event-synthesis path so
  // existing drag handlers (markerDragRef / dragRef) continue to work.
  // We deliberately leave the wall/room/door drafting "tap to add point"
  // path off on touch — geometry edits happen on desktop, mobile is
  // for inspection + marker placement.
  const pinchRef = useRef<{ lastDist: number } | null>(null);
  const panRef = useRef<{ lastX: number; lastY: number } | null>(null);
  const onCanvasTouchStart = (e: React.TouchEvent<SVGSVGElement>) => {
    if (e.touches.length === 2) {
      const [a, b] = [e.touches[0], e.touches[1]];
      pinchRef.current = { lastDist: Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY) };
      panRef.current = null;
      markerDragRef.current = null;
      e.preventDefault();
      return;
    }
    if (e.touches.length === 1) {
      const target = e.target as Element;
      // If the touch landed on a marker, mirror onMarkerMouseDown's
      // behaviour: set markerDragRef so onCanvasTouchMove can drive
      // marker-position updates from touch coords (mousemove doesn't
      // fire during single-finger drag, so we have to wire it up
      // explicitly).
      const markerEl = target.closest("[data-marker-id]");
      if (markerEl) {
        const markerId = markerEl.getAttribute("data-marker-id");
        const m = markerId ? markers.find((mm) => mm.id === markerId) : null;
        if (m) {
          markerDragRef.current = { id: m.id, startX: m.position_x, startY: m.position_y };
          // No preventDefault here — let the synthesized mouse events
          // also fire if the user actually does a quick tap (which our
          // mousedown handler will then no-op on).
          return;
        }
      }
      // Tapped a wall / room / door / corridor / entry-point /
      // label — let the existing mouse-event-synthesis path drive
      // their handlers (those don't typically need to follow a finger
      // drag through the entire viewport, just need a tap).
      if (target.closest("[data-entity-kind]")) return;
      // Blank canvas → pan.
      panRef.current = { lastX: e.touches[0].clientX, lastY: e.touches[0].clientY };
    }
  };
  const onCanvasTouchMove = (e: React.TouchEvent<SVGSVGElement>) => {
    if (pinchRef.current && e.touches.length === 2) {
      const [a, b] = [e.touches[0], e.touches[1]];
      const d = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
      if (pinchRef.current.lastDist > 0) {
        const factor = d / pinchRef.current.lastDist;
        // Clamp per-frame factor to avoid huge jumps on jittery input.
        const clamped = Math.max(0.5, Math.min(2.0, factor));
        dispatch({ type: "zoom", factor: clamped });
      }
      pinchRef.current.lastDist = d;
      e.preventDefault();
      return;
    }
    if (markerDragRef.current && e.touches.length === 1) {
      // Drive marker position from the live touch — same logic as
      // onMarkerMouseMove but reading from e.touches[0]. Snapshot the
      // id BEFORE setMarkers (the updater runs lazily; touchend may
      // clear markerDragRef.current before React processes the queue).
      const dragId = markerDragRef.current.id;
      const t = e.touches[0];
      const pt = toImageSpace(t.clientX, t.clientY);
      setMarkers((prev) => prev.map((m) =>
        m.id === dragId ? { ...m, position_x: pt.x, position_y: pt.y } : m,
      ));
      e.preventDefault();
      return;
    }
    if (panRef.current && e.touches.length === 1) {
      const dx = e.touches[0].clientX - panRef.current.lastX;
      const dy = e.touches[0].clientY - panRef.current.lastY;
      // Divide by zoom so the pan tracks the user's finger 1:1 even
      // when zoomed in/out (state.pan is in pre-scale units).
      dispatch({ type: "pan", dx: dx / state.zoom, dy: dy / state.zoom });
      panRef.current.lastX = e.touches[0].clientX;
      panRef.current.lastY = e.touches[0].clientY;
      e.preventDefault();
    }
  };
  const onCanvasTouchEnd = (e: React.TouchEvent<SVGSVGElement>) => {
    if (e.touches.length < 2) pinchRef.current = null;
    if (e.touches.length === 0) {
      panRef.current = null;
      // Finalize a marker drag if one was in progress — same patchMarker
      // round-trip as the mouse path.
      if (markerDragRef.current) void onMarkerMouseUp();
    }
  };

  // ── Handle drag for selected entity vertices ──
  const dragRef = useRef<{ kind: SelectionId["kind"]; id: string; vIdx?: number; field?: "hinge" | "swing_to" } | null>(null);
  const onPointDown = (kind: SelectionId["kind"], id: string, vIdx?: number, field?: "hinge" | "swing_to") => (e: React.MouseEvent) => {
    e.stopPropagation();
    if (state.tool !== "select") return;
    dragRef.current = { kind, id, vIdx, field };
    dispatch({ type: "select", sel: { kind, id, vertexIndex: vIdx } });
  };
  const onCanvasMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    // Mouse pan — top priority. Walks state.pan by the client-space
    // delta scaled by zoom so 1 px movement of the cursor = 1 px on
    // the underlying image regardless of how zoomed-in we are.
    if (mousePanRef.current) {
      const dx = e.clientX - mousePanRef.current.lastX;
      const dy = e.clientY - mousePanRef.current.lastY;
      dispatch({ type: "pan", dx: dx / state.zoom, dy: dy / state.zoom });
      mousePanRef.current.lastX = e.clientX;
      mousePanRef.current.lastY = e.clientY;
      return;
    }
    // Marquee drag — has priority since it owns its own canvas-wide
    // gesture and never coexists with marker / vertex drag.
    if (state.marquee) {
      const pt = toImageSpace(e.clientX, e.clientY);
      dispatch({ type: "marquee_update", pt });
      return;
    }
    // Marker drag has priority over the geometry-vertex drag — the
    // marker mousedown handler set markerDragRef and stopPropagation,
    // so we won't have both refs set simultaneously.
    if (markerDragRef.current) { onMarkerMouseMove(e); return; }
    if (!dragRef.current) return;
    const pt = toImageSpace(e.clientX, e.clientY);
    const d = dragRef.current;
    if (d.kind === "wall" && d.vIdx != null) {
      const wall = state.layout.walls.find((w) => w.id === d.id);
      if (!wall) return;
      const points = [...wall.points] as [LayoutPoint, LayoutPoint];
      points[d.vIdx] = pt;
      replaceWall(d.id, { points });
    } else if (d.kind === "room" && d.vIdx != null) {
      replaceRoomVertex(d.id, d.vIdx, pt);
    } else if (d.kind === "door" && d.field) {
      replaceDoor(d.id, { [d.field]: pt });
    }
  };
  const onCanvasMouseUp = () => {
    if (mousePanRef.current) { mousePanRef.current = null; return; }
    if (state.marquee) {
      // Commit the marquee selection: every entity (wall vertex / room
      // / door / corridor / entry / label) whose anchor falls inside
      // the rectangle gets picked. Empty drag (e.g. accidental click in
      // marquee mode) just clears the rect.
      const sel = entitiesInsideRect(state.layout, state.marquee);
      if (sel.length === 0) dispatch({ type: "marquee_clear" });
      else dispatch({ type: "marquee_end", selection: sel });
      return;
    }
    if (markerDragRef.current) { void onMarkerMouseUp(); return; }
    dragRef.current = null;
  };

  // ── Render ──
  const viewBox = useMemo(() => {
    const w = state.layout.image_size.width;
    const h = state.layout.image_size.height;
    return `0 0 ${w} ${h}`;
  }, [state.layout.image_size]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => navigate(`/dashboard/sites/${siteId}/floor-stack`)} className="btn-ghost text-sm">
          <ArrowLeft className="h-4 w-4" /> 回案場
        </button>
        <h1 className="text-base font-semibold text-text-primary flex-1 truncate">
          樓層平面圖編輯器
        </h1>
        <div className="text-2xs text-text-muted">
          {state.dirty ? "尚未儲存" : "已同步"}
          {state.parseCostUsd != null ? ` · 上次解析 $${state.parseCostUsd.toFixed(4)}` : ""}
        </div>
      </div>

      {/* Toolbar — hidden entirely in 3D mode (per operator: "3D 不可選取
          編輯 工具列直接 hide"). The 3D canvas itself carries a minimal
          floating "切回 2D" button so the user can always get back. */}
      <div
        className="card p-2 flex flex-wrap items-center gap-1"
        style={{ display: viewMode === "3d" ? "none" : undefined }}
      >
        <SelectToolGroup tool={state.tool} onPick={(t) => dispatch({ type: "tool", tool: t })} />
        <ToolButton active={state.tool === "wall"}   onClick={() => dispatch({ type: "tool", tool: "wall" })}   icon={<Pencil className="h-4 w-4" />}      label="加牆"   hint="W" />
        <ToolButton active={state.tool === "room"}   onClick={() => dispatch({ type: "tool", tool: "room" })}   icon={<Square className="h-4 w-4" />}      label="加房間" hint="R" />
        <ToolButton active={state.tool === "door"}   onClick={() => dispatch({ type: "tool", tool: "door" })}   icon={<DoorClosed className="h-4 w-4" />}  label="加門"   hint="D" />
        <span className="mx-1 h-6 w-px bg-border" />
        <button className="btn-ghost text-xs" onClick={() => dispatch({ type: "undo" })} disabled={state.historyIndex <= 0}><Undo2 className="h-4 w-4" /></button>
        <button className="btn-ghost text-xs" onClick={() => dispatch({ type: "redo" })} disabled={state.historyIndex >= state.history.length - 1}><Redo2 className="h-4 w-4" /></button>
        <button
          className="btn-ghost text-xs"
          onClick={() => {
            if (state.marqueeSelection && state.marqueeSelection.length > 0) deleteMarqueeSelection();
            else if (state.selection) deleteSelection();
          }}
          disabled={!state.selection && !(state.marqueeSelection && state.marqueeSelection.length > 0)}
          title={state.marqueeSelection && state.marqueeSelection.length > 0
            ? `刪除框選的 ${state.marqueeSelection.length} 個項目`
            : "刪除目前選中項目"}
        >
          <Trash2 className="h-4 w-4" /> 刪除選中
          {state.marqueeSelection && state.marqueeSelection.length > 0 && (
            <span className="ml-1 px-1 rounded bg-warning/20 text-warning text-2xs font-mono">{state.marqueeSelection.length}</span>
          )}
        </button>
        <span className="mx-1 h-6 w-px bg-border" />
        <button className="btn-ghost text-xs" onClick={() => dispatch({ type: "zoom", factor: 1.2 })} title="放大"><ZoomIn className="h-4 w-4" /></button>
        <button className="btn-ghost text-xs" onClick={() => dispatch({ type: "zoom", factor: 1 / 1.2 })} title="縮小"><ZoomOut className="h-4 w-4" /></button>
        <button className="btn-ghost text-xs" onClick={() => dispatch({ type: "reset_view" })} title="重置縮放與位移">1:1</button>
        <span className="text-2xs text-text-muted hidden sm:inline ml-1" title="按住 Space 鍵或滑鼠中鍵拖曳即可平移畫布">
          按 Space 拖曳平移
        </span>
        <span className="mx-1 h-6 w-px bg-border" />
        <button
          className={`btn-ghost text-xs ${markerPanelOpen ? "bg-accent/15 text-accent border border-accent/40" : ""}`}
          onClick={() => setMarkerPanelOpen((v) => !v)}
          title="顯示 / 編輯地點清單"
          disabled={!state.versionId}
        >
          <Tag className="h-4 w-4" /> 地點 ({markers.filter((m) => m.status === "active").length})
        </button>
        <span className="mx-1 h-6 w-px bg-border" />
        {/* 2D / 3D view toggle. 3D is read-only for geometry; only marker
            drag is supported there. Tooltip explains. */}
        <button
          className={`btn-ghost text-xs ${viewMode === "3d" ? "bg-accent/15 text-accent border border-accent/40" : ""}`}
          onClick={() => {
            if (viewMode === "3d") closeTo2D();
            else setViewMode("3d");
          }}
          title={viewMode === "2d" ? "切換成 3D 立體檢視（地點仍可拖移；牆 / 房間 / 門編輯請回 2D）" : "切回 2D 平面編輯"}
          disabled={!state.versionId}
        >
          {viewMode === "2d" ? <><Box className="h-4 w-4" /> 3D</> : <><MapIcon className="h-4 w-4" /> 2D</>}
        </button>
        <span className="mx-1 h-6 w-px bg-border" />
        <button className="btn-secondary text-xs" onClick={onPickFile}><Upload className="h-4 w-4" /> 上傳新圖</button>
        <button className="btn-secondary text-xs" onClick={() => void onReparse()} disabled={reparsing || !state.versionId}>
          {reparsing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} 重新解析
        </button>
        <button
          className="btn-ghost text-xs text-rose-400 hover:bg-rose-500/10"
          onClick={() => void onClearBaseMap()}
          disabled={clearing || !state.versionId}
          title="封存所有底圖版本，回到空白狀態以重新上傳。已放置的地點會在下次成功解析後自動接回。"
        >
          {clearing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} 清除底圖
        </button>
        <button className="btn-ghost text-xs" onClick={onExportSvg}><Download className="h-4 w-4" /> 匯出 SVG</button>
        <button className="btn-ghost text-xs" onClick={onExportJson}><Download className="h-4 w-4" /> 匯出 JSON</button>
        <span className="flex-1" />
        <button className="btn-primary text-xs" onClick={() => void onSave()} disabled={saving || !state.dirty}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} 儲存
        </button>
      </div>

      {/* Layer toggles — also hidden in 3D mode. */}
      <div
        className="card p-2 flex flex-wrap items-center gap-2 text-2xs"
        style={{ display: viewMode === "3d" ? "none" : undefined }}
      >
        <Layers className="h-3.5 w-3.5 text-text-muted" />
        {(Object.keys(state.layers) as LayerKey[]).map((k) => (
          <label key={k} className="inline-flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={state.layers[k]} onChange={() => dispatch({ type: "toggle_layer", layer: k })} />
            <span>{LAYER_LABEL[k]}</span>
          </label>
        ))}
        <span className="ml-auto text-text-muted">
          牆 {state.layout.walls.length} · 房 {state.layout.rooms.length} · 門 {state.layout.doors.length} ·
          走道 {state.layout.corridors.length} · 入口 {state.layout.entry_points.length} · 文字 {state.layout.labels.length}
        </span>
      </div>

      {/* Toast */}
      {toast && (
        <div className={`card p-3 flex items-center gap-2 ${toast.kind === "ok" ? "border-success/40 bg-success/5 text-success" : "border-danger/40 bg-danger/5 text-danger"}`}>
          {toast.kind === "ok" ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
          <span className="text-sm">{toast.msg}</span>
          <button className="ml-auto btn-ghost text-2xs" onClick={() => setToast(null)}>關閉</button>
        </div>
      )}

      {/* Canvas + drop zone */}
      <div className="card p-0 overflow-hidden relative" style={{ minHeight: 480 }}>
        {state.parseStatus === "idle" && state.layout === EMPTY_LAYOUT && state.history.length === 1 && state.history[0] === EMPTY_LAYOUT && (
          <DropZone onPick={onPickFile} onDrop={onDropFile} />
        )}
        {(state.parseStatus === "uploading" || state.parseStatus === "parsing") && (
          <ParseOverlay
            phase={state.parseStatus}
            uploadPercent={state.uploadPercent}
            uploadBytesLoaded={state.uploadBytesLoaded}
            uploadBytesTotal={state.uploadBytesTotal}
            parsingStartedAt={state.parsingStartedAt}
          />
        )}

        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/jpg,application/pdf"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void onDropFile(f);
            e.target.value = "";
          }}
        />

        {viewMode === "3d" && state.parseStatus === "ready" && (
          <div className="relative">
            <Suspense
              fallback={
                <div className="grid place-items-center bg-bg-deep w-full h-[60vh]">
                  <div className="text-center text-sm text-text-secondary">
                    <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2 text-accent" />
                    載入 3D 視圖中⋯
                  </div>
                </div>
              }
            >
              <FloorLayout3D
                layout={state.layout}
                markers={markers}
                locationLookup={locationLookup}
                onMarkerMove={(id, pos) => void onMarkerMove3D(id, pos)}
                onContextLost={() => {
                  // r3f Canvas dispose calls forceContextLoss() during
                  // unmount, so this listener also fires when the user
                  // clicks 「切回 2D」 — not just on real GPU drops.
                  // Suppress the toast for operator-initiated closes.
                  if (userClosed3dRef.current) {
                    userClosed3dRef.current = false;
                    return;
                  }
                  setViewMode("2d");
                  setToast({ kind: "err", msg: "3D 視圖暫時不可用（瀏覽器 WebGL 失效），已切回 2D。" });
                }}
              />
            </Suspense>
            {/* Floating "back to 2D" button — only way out of 3D mode
                since the toolbar is hidden in this view. */}
            <button
              onClick={closeTo2D}
              className="btn-primary text-xs absolute top-3 right-3 shadow-lg"
              title="切回 2D 編輯模式"
            >
              <MapIcon className="h-4 w-4" /> 切回 2D
            </button>
          </div>
        )}

        <div
          className="relative overflow-hidden bg-bg-deep"
          onDrop={(e) => {
            e.preventDefault();
            const f = e.dataTransfer.files?.[0];
            if (f) void onDropFile(f);
          }}
          onDragOver={(e) => e.preventDefault()}
          style={{
            touchAction: "none",
            cursor: spacePanning ? "grab" : "default",
            display: viewMode === "3d" ? "none" : undefined,
          }}
        >
          <svg
            ref={svgRef}
            viewBox={viewBox}
            preserveAspectRatio="xMidYMid meet"
            className="block w-full h-[60vh]"
            style={{ transform: `scale(${state.zoom}) translate(${state.pan.x}px, ${state.pan.y}px)`, transformOrigin: "center" }}
            onMouseDown={onCanvasMouseDown}
            onDoubleClick={onCanvasDoubleClick}
            onMouseMove={onCanvasMouseMove}
            onMouseUp={onCanvasMouseUp}
            onWheel={onCanvasWheel}
            onTouchStart={onCanvasTouchStart}
            onTouchMove={onCanvasTouchMove}
            onTouchEnd={onCanvasTouchEnd}
            onTouchCancel={onCanvasTouchEnd}
          >
            {/* Grid background */}
            <defs>
              <pattern id="grid" width={50} height={50} patternUnits="userSpaceOnUse">
                <path d="M 50 0 L 0 0 0 50" fill="none" stroke="rgba(0, 212, 255, 0.06)" strokeWidth={1} />
              </pattern>
            </defs>
            <rect x={0} y={0} width={state.layout.image_size.width} height={state.layout.image_size.height} fill="url(#grid)" />

            {/* Layers */}
            {state.layers.rooms && state.layout.rooms.map((r) => (
              <RoomNode key={r.id} room={r} selected={state.selection?.kind === "room" && state.selection.id === r.id}
                onSelect={() => dispatch({ type: "select", sel: { kind: "room", id: r.id } })}
                onPointDown={(idx) => onPointDown("room", r.id, idx)}
              />
            ))}

            {state.layers.corridors && state.layout.corridors.map((c) => (
              <polyline key={c.id}
                data-entity-kind="corridor" data-entity-id={c.id}
                points={c.centerline.map((p) => `${p.x},${p.y}`).join(" ")}
                fill="none" stroke="#00d4ff" strokeWidth={c.width_px || 4} strokeOpacity={0.18}
                onClick={() => dispatch({ type: "select", sel: { kind: "corridor", id: c.id } })}
              />
            ))}

            {state.layers.walls && state.layout.walls.map((w) => (
              <WallNode key={w.id} wall={w} selected={state.selection?.kind === "wall" && state.selection.id === w.id}
                onSelect={() => dispatch({ type: "select", sel: { kind: "wall", id: w.id } })}
                onPointDown={(idx) => onPointDown("wall", w.id, idx)}
              />
            ))}

            {state.layers.doors && state.layout.doors.map((d) => (
              <DoorNode key={d.id} door={d} selected={state.selection?.kind === "door" && state.selection.id === d.id}
                onSelect={() => dispatch({ type: "select", sel: { kind: "door", id: d.id } })}
                onPointDown={(field) => onPointDown("door", d.id, undefined, field)}
              />
            ))}

            {state.layers.entry_points && state.layout.entry_points.map((p) => (
              <g key={p.id} data-entity-kind="entry_point" data-entity-id={p.id}
                onClick={(e) => { e.stopPropagation(); dispatch({ type: "select", sel: { kind: "entry_point", id: p.id } }); }}
              >
                <circle cx={p.point.x} cy={p.point.y} r={10} fill="#fbbf24" fillOpacity={0.85} stroke="#0b0d12" strokeWidth={2} />
                {p.label && <text x={p.point.x + 12} y={p.point.y + 4} fill="#fbbf24" fontSize={12}>{p.label}</text>}
              </g>
            ))}

            {state.layers.labels && state.layout.labels.map((l) => (
              <text key={l.id} data-entity-kind="label" data-entity-id={l.id}
                x={l.point.x} y={l.point.y}
                fill="#e5e7eb" fontSize={l.font_size_px || 14}
                transform={l.rotation_deg ? `rotate(${l.rotation_deg} ${l.point.x} ${l.point.y})` : undefined}
                onClick={(e) => { e.stopPropagation(); dispatch({ type: "select", sel: { kind: "label", id: l.id } }); }}
              >
                {l.text}
              </text>
            ))}

            {/* Crosshair while placing — visual cue that the next click
                creates a marker. Pointer events disabled so it doesn't
                interfere with the underlying mousedown. */}
            {placingLocationId && (
              <g pointerEvents="none">
                <text
                  x={state.layout.image_size.width / 2}
                  y={24}
                  textAnchor="middle"
                  fill="#00d4ff"
                  fontSize={14}
                >
                  點地圖任意位置以放置地點（按 ESC 取消）
                </text>
              </g>
            )}

            {/* In-progress marquee drag — dashed accent rectangle. */}
            {state.marquee && (
              (() => {
                const r = normaliseRect(state.marquee);
                return (
                  <rect
                    x={r.minX} y={r.minY}
                    width={r.maxX - r.minX} height={r.maxY - r.minY}
                    fill="rgba(0,212,255,0.08)"
                    stroke="#00d4ff"
                    strokeDasharray="4 3"
                    strokeWidth={1}
                    pointerEvents="none"
                  />
                );
              })()
            )}

            {/* Marquee committed selection — translucent halos on every
                picked entity so the user sees what they grabbed before
                pressing Delete. */}
            {state.marqueeSelection && state.marqueeSelection.length > 0 && (
              <g pointerEvents="none">
                {state.marqueeSelection.map((s, i) => {
                  if (s.kind === "wall") {
                    const w = state.layout.walls.find((x) => x.id === s.id);
                    if (!w) return null;
                    return (
                      <line key={`mq-${i}`}
                        x1={w.points[0].x} y1={w.points[0].y}
                        x2={w.points[1].x} y2={w.points[1].y}
                        stroke="#facc15" strokeWidth={4} strokeOpacity={0.55} strokeLinecap="round"
                      />
                    );
                  }
                  if (s.kind === "room") {
                    const r = state.layout.rooms.find((x) => x.id === s.id);
                    if (!r || r.vertices.length < 3) return null;
                    return (
                      <polygon key={`mq-${i}`}
                        points={r.vertices.map((v) => `${v.x},${v.y}`).join(" ")}
                        fill="rgba(250,204,21,0.18)" stroke="#facc15" strokeWidth={2}
                      />
                    );
                  }
                  if (s.kind === "door") {
                    const d = state.layout.doors.find((x) => x.id === s.id);
                    if (!d) return null;
                    return <circle key={`mq-${i}`} cx={d.hinge.x} cy={d.hinge.y} r={9} fill="none" stroke="#facc15" strokeWidth={2.5} />;
                  }
                  if (s.kind === "corridor") {
                    const c = state.layout.corridors.find((x) => x.id === s.id);
                    if (!c || c.centerline.length < 2) return null;
                    return (
                      <polyline key={`mq-${i}`}
                        points={c.centerline.map((p) => `${p.x},${p.y}`).join(" ")}
                        fill="none" stroke="#facc15" strokeWidth={Math.max(2, c.width_px ?? 4)}
                        strokeOpacity={0.6} strokeLinecap="round" strokeLinejoin="round"
                      />
                    );
                  }
                  if (s.kind === "entry_point") {
                    const ep = state.layout.entry_points.find((x) => x.id === s.id);
                    if (!ep) return null;
                    return <circle key={`mq-${i}`} cx={ep.point.x} cy={ep.point.y} r={11} fill="none" stroke="#facc15" strokeWidth={2.5} />;
                  }
                  if (s.kind === "label") {
                    const lbl = state.layout.labels.find((x) => x.id === s.id);
                    if (!lbl) return null;
                    return <circle key={`mq-${i}`} cx={lbl.point.x} cy={lbl.point.y} r={9} fill="none" stroke="#facc15" strokeWidth={2.5} />;
                  }
                  return null;
                })}
                <text
                  x={normaliseRect(state.marquee ?? { x1: 0, y1: 0, x2: 0, y2: 0 }).minX + 4}
                  y={normaliseRect(state.marquee ?? { x1: 0, y1: 0, x2: 0, y2: 0 }).minY - 6}
                  fill="#facc15"
                  fontSize={11}
                  fontWeight={600}
                />
              </g>
            )}

            {/* In-progress drafting */}
            {state.drafting && state.drafting.length > 0 && (
              <g pointerEvents="none">
                {state.drafting.map((p, i) => (
                  <circle key={i} cx={p.x} cy={p.y} r={4} fill="#00d4ff" />
                ))}
                {state.tool === "wall" && state.drafting.length === 1 && (
                  <text x={state.drafting[0]!.x + 8} y={state.drafting[0]!.y - 8} fill="#00d4ff" fontSize={12}>點選第二個端點以結束牆</text>
                )}
                {state.tool === "room" && state.drafting.length >= 1 && (
                  <>
                    <polyline points={state.drafting.map((p) => `${p.x},${p.y}`).join(" ")} fill="rgba(0,212,255,0.05)" stroke="#00d4ff" strokeDasharray="4 4" />
                    <text x={state.drafting[0]!.x + 8} y={state.drafting[0]!.y - 8} fill="#00d4ff" fontSize={12}>連點加入頂點，雙擊結束（至少 3 點）</text>
                  </>
                )}
              </g>
            )}

            {/* Markers layer — rendered ABSOLUTELY LAST inside the SVG so
                it always paints on top of walls / rooms / doors / labels
                / drafting. SVG uses painter's algorithm; the last sibling
                wins. Renders every active marker (location / camera /
                aed / exit) — the user-facing label is "地點" but the
                underlying map_markers table carries multiple types and
                operators expect to see them all on the canvas. */}
            <g data-marker-layer style={{ filter: "drop-shadow(0 1px 3px rgba(0,0,0,0.6))" }}>
              {markers.filter((m) => m.status === "active").map((m) => {
                const cp = siteLocations.find((c) => c.id === m.linked_location_id);
                const code = m.label ?? cp?.code ?? markerTypeIcon(m.marker_type).short;
                const cpLabel = cp?.label ?? null;
                const palette = markerColours(m.marker_type);
                return (
                  <g
                    key={m.id}
                    data-entity-kind="marker"
                    data-marker-id={m.id}
                    transform={`translate(${m.position_x}, ${m.position_y})`}
                    style={{ cursor: "grab" }}
                    onMouseDown={onMarkerMouseDown(m.id)}
                  >
                    {/* White halo so it pops against any background — */}
                    <circle r={20} fill="#ffffff" fillOpacity={0.92} />
                    {/* Coloured disc */}
                    <circle r={16} fill={palette.fill} stroke={palette.stroke} strokeWidth={2.5} />
                    {/* Type indicator inside the disc — the SENTRY logo for
                        地點 (location), a single-letter glyph for other
                        marker types. Using <image href="/sentry-icon.svg"> 
                        keeps the brand mark consistent across the
                        product (sidebar, login, scan pages all reference
                        the same asset). */}
                    {m.marker_type === "location" ? (
                      <image
                        href="/sentry-icon.svg"
                        x={-11}
                        y={-11}
                        width={22}
                        height={22}
                        style={{ pointerEvents: "none" }}
                      />
                    ) : (
                      <text
                        x={0}
                        y={5}
                        textAnchor="middle"
                        fill={palette.fg}
                        fontSize={14}
                        fontWeight={700}
                        style={{ pointerEvents: "none" }}
                      >
                        {markerTypeIcon(m.marker_type).short}
                      </text>
                    )}
                    {/* Code/label to the right of the disc */}
                    <text
                      x={22}
                      y={2}
                      fill="#0b0d12"
                      fontSize={13}
                      fontWeight={700}
                      stroke="#ffffff"
                      strokeWidth={3}
                      paintOrder="stroke"
                      style={{ pointerEvents: "none" }}
                    >
                      {code}
                    </text>
                    {cpLabel && (
                      <text
                        x={22}
                        y={18}
                        fill="#0b0d12"
                        fontSize={11}
                        fontWeight={500}
                        stroke="#ffffff"
                        strokeWidth={3}
                        paintOrder="stroke"
                        style={{ pointerEvents: "none" }}
                      >
                        {cpLabel}
                      </text>
                    )}
                  </g>
                );
              })}
            </g>
          </svg>
        </div>
      </div>

      {/* Selection inspector — basic label / kind editor */}
      {state.selection && (
        <SelectionInspector
          state={state}
          onChange={(layout) => dispatch({ type: "set_layout", layout })}
          onClose={() => dispatch({ type: "select", sel: null })}
        />
      )}

      {markerPanelOpen && (
        <LocationMarkerPanel
          locations={siteLocations}
          markers={markers}
          placingId={placingLocationId}
          onStartPlacing={(id) => {
            setPlacingLocationId(id);
            setMarkerPanelOpen(false);
            setToast({ kind: "ok", msg: "點地圖任意位置以放置這個地點（按 ESC 取消）。" });
          }}
          onRemove={(markerId) => void removeLocationMarker(markerId)}
          onClose={() => setMarkerPanelOpen(false)}
        />
      )}
    </div>
  );
}

// ── Sub-components ───────────────────────────────────────────────────────

const LAYER_LABEL: Record<LayerKey, string> = {
  walls: "牆",
  rooms: "房間",
  doors: "門",
  corridors: "走道",
  entry_points: "入口",
  labels: "文字",
};

interface LocationMarkerPanelProps {
  locations: LocationRow[];
  markers: MapMarkerDto[];
  placingId: string | null;
  onStartPlacing: (locationId: string) => void;
  onRemove: (markerId: string) => void;
  onClose: () => void;
}

/**
 * Right-side drawer listing every active site location with its
 * placement state on the current floor. Two visual buckets:
 *
 *   1. 已放置：marker exists on this floor → show marker.label /
 *      location code + a "從地圖移除" archive button.
 *   2. 未放置：no active marker for this location → show "點此放置"
 *      button which puts the editor into placement mode (next canvas
 *      click creates the marker).
 *
 * The panel is presentational — every action is delegated to the
 * parent (createMarker / archiveMarker / patchMarker) which owns the
 * canonical marker state. Placement-mode UX intentionally closes the
 * panel so the user has the whole canvas visible.
 */
function LocationMarkerPanel({
  locations, markers, placingId, onStartPlacing, onRemove, onClose,
}: LocationMarkerPanelProps): React.ReactElement {
  const placedByCpId = useMemo(() => {
    const m = new Map<string, MapMarkerDto>();
    for (const mk of markers) {
      if (mk.marker_type === "location" && mk.status === "active" && mk.linked_location_id) {
        m.set(mk.linked_location_id, mk);
      }
    }
    return m;
  }, [markers]);

  const placed = locations.filter((c) => placedByCpId.has(c.id));
  const unplaced = locations.filter((c) => !placedByCpId.has(c.id));

  return (
    <aside
      className="fixed right-4 top-24 z-30 w-[320px] max-h-[calc(100vh-8rem)] overflow-hidden rounded-lg border border-border bg-bg-surface shadow-xl flex flex-col"
      data-testid="location-marker-panel"
    >
      <header className="flex items-center justify-between border-b border-border px-3 py-2">
        <h3 className="text-sm font-semibold text-text-primary">地點清單</h3>
        <button onClick={onClose} className="btn-ghost p-1" aria-label="關閉">
          <X className="h-4 w-4" />
        </button>
      </header>

      <div className="overflow-y-auto px-3 py-2 space-y-3 text-sm">
        <section>
          <h4 className="mb-1.5 text-xs uppercase tracking-wider text-text-muted">
            已放置（{placed.length}）
          </h4>
          {placed.length === 0 ? (
            <p className="text-xs text-text-muted">這層樓還沒放任何地點。</p>
          ) : (
            <ul className="space-y-1.5">
              {placed.map((c) => {
                const m = placedByCpId.get(c.id)!;
                return (
                  <li key={c.id} className="flex items-center justify-between gap-2 rounded border border-border bg-bg-base px-2 py-1.5">
                    <div className="min-w-0 flex-1">
                      <div className="font-mono text-text-primary">{c.code}</div>
                      <div className="truncate text-xs text-text-secondary">{c.label}</div>
                    </div>
                    <button
                      onClick={() => onRemove(m.id)}
                      className="btn-ghost text-xs text-rose-400 hover:bg-rose-500/10"
                      title="從地圖移除（不會刪除地點）"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <section>
          <h4 className="mb-1.5 text-xs uppercase tracking-wider text-text-muted">
            未放置（{unplaced.length}）
          </h4>
          {unplaced.length === 0 ? (
            <p className="text-xs text-text-muted">這個案場所有地點都已放上地圖。</p>
          ) : (
            <ul className="space-y-1.5">
              {unplaced.map((c) => (
                <li
                  key={c.id}
                  className={`flex items-center justify-between gap-2 rounded border px-2 py-1.5 ${
                    placingId === c.id
                      ? "border-accent/60 bg-accent/10"
                      : "border-border bg-bg-base"
                  }`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-mono text-text-primary">{c.code}</div>
                    <div className="truncate text-xs text-text-secondary">{c.label}</div>
                  </div>
                  <button
                    onClick={() => onStartPlacing(c.id)}
                    className="btn-secondary text-xs"
                    disabled={placingId !== null && placingId !== c.id}
                  >
                    點此放置
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <footer className="border-t border-border px-3 py-2 text-[11px] text-text-muted">
        放置後可在地圖上拖曳調整位置；按 ESC 取消放置模式。
      </footer>
    </aside>
  );
}

interface ParseOverlayProps {
  phase: "uploading" | "parsing";
  uploadPercent: number;
  uploadBytesLoaded: number;
  uploadBytesTotal: number;
  parsingStartedAt: number | null;
}

/**
 * Two-phase upload overlay:
 *   1. uploading — real progress bar driven by XHR.upload progress events;
 *      shows "X / Y MB" and "% 已上傳".
 *   2. parsing  — bytes are at the worker, OpenAI is now thinking. We
 *      can't see real progress (single blocking call) so we show an
 *      elapsed-time counter against AI_EXPECTED_SECONDS, plus a
 *      striped indeterminate bar.
 *
 * The upper banner stays consistent through both phases ("圖片解析") so
 * the user knows it's the same logical operation.
 */
function ParseOverlay({
  phase, uploadPercent, uploadBytesLoaded, uploadBytesTotal, parsingStartedAt,
}: ParseOverlayProps): React.ReactElement {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (phase !== "parsing" || !parsingStartedAt) return;
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, [phase, parsingStartedAt]);

  const pctNum = Math.round(uploadPercent * 100);
  const mbLoaded = (uploadBytesLoaded / 1024 / 1024).toFixed(1);
  const mbTotal = uploadBytesTotal > 0 ? (uploadBytesTotal / 1024 / 1024).toFixed(1) : "—";

  const elapsedSec = parsingStartedAt ? Math.max(0, (now - parsingStartedAt) / 1000) : 0;
  const elapsedDisplay = elapsedSec.toFixed(0);
  const aiPctClamped = Math.min(0.95, elapsedSec / AI_EXPECTED_SECONDS); // never hit 100% before the response lands

  return (
    <div className="absolute inset-0 z-10 grid place-items-center bg-bg-base/85 backdrop-blur-sm">
      <div className="w-[min(92%,420px)] rounded-lg border border-border bg-bg-surface p-5 text-sm">
        <div className="mb-3 flex items-center gap-2 text-text-primary">
          <Loader2 className="h-5 w-5 animate-spin text-accent" />
          <span className="font-semibold">圖片解析中</span>
        </div>

        {/* Phase 1: upload */}
        <div className={phase === "uploading" ? "" : "opacity-50"}>
          <div className="flex items-baseline justify-between mb-1">
            <span className="text-xs text-text-secondary">
              {phase === "uploading" ? "1 ／ 2 上傳圖片到伺服器" : "1 ／ 2 上傳圖片 ✓"}
            </span>
            <span className="text-xs font-mono tabular-nums text-text-muted">
              {phase === "uploading" ? `${mbLoaded} ／ ${mbTotal} MB ・ ${pctNum}%` : `${mbTotal} MB ✓`}
            </span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-bg-deep">
            <div
              className="h-full rounded-full bg-accent transition-[width] duration-150 ease-out"
              style={{ width: `${phase === "uploading" ? pctNum : 100}%` }}
              role="progressbar"
              aria-valuenow={phase === "uploading" ? pctNum : 100}
              aria-valuemin={0}
              aria-valuemax={100}
            />
          </div>
        </div>

        {/* Phase 2: AI thinking */}
        <div className={`mt-4 ${phase === "parsing" ? "" : "opacity-40"}`}>
          <div className="flex items-baseline justify-between mb-1">
            <span className="text-xs text-text-secondary">
              {phase === "parsing" ? "2 ／ 2 AI 分析平面圖" : "2 ／ 2 AI 分析平面圖（等待中）"}
            </span>
            {phase === "parsing" && (
              <span className="text-xs font-mono tabular-nums text-text-muted">
                已等候 {elapsedDisplay}s ／ 預估 {AI_EXPECTED_SECONDS}s
              </span>
            )}
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-bg-deep">
            <div
              className={`h-full rounded-full ${phase === "parsing" ? "bg-accent/80" : "bg-bg-deep"}`}
              style={{
                width: `${Math.round(aiPctClamped * 100)}%`,
                transition: "width 0.5s linear",
                backgroundImage: phase === "parsing"
                  ? "linear-gradient(45deg, rgba(255,255,255,0.15) 25%, transparent 25%, transparent 50%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.15) 75%, transparent 75%)"
                  : undefined,
                backgroundSize: phase === "parsing" ? "16px 16px" : undefined,
              }}
            />
          </div>
          <p className="mt-2 text-2xs text-text-muted">
            使用 OpenAI 視覺模型解析牆 / 房間 / 門 / 走道。圖片越複雜越久；超過 90 秒會自動中止。
          </p>
        </div>
      </div>
    </div>
  );
}

function ToolButton(props: { active: boolean; onClick: () => void; icon: React.ReactNode; label: string; hint: string }): React.ReactElement {
  return (
    <button
      className={`btn-ghost text-xs ${props.active ? "bg-accent/15 text-accent border border-accent/40" : ""}`}
      onClick={props.onClick}
      title={`${props.label}（快捷鍵 ${props.hint}）`}
    >
      {props.icon} {props.label}
    </button>
  );
}

/** Split-button for the two select modes — one click area to set
 *  whichever sub-mode the user picked last, plus a chevron that opens
 *  the submenu so they can switch between 點擊選取 / 框選 without
 *  remembering the V / M shortcuts. */
function SelectToolGroup({ tool, onPick }: {
  tool: EditorTool;
  onPick: (t: "select" | "select_marquee") => void;
}): React.ReactElement {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement | null>(null);
  const isClick = tool === "select";
  const isMarquee = tool === "select_marquee";
  const active = isClick || isMarquee;
  const subLabel = isMarquee ? "框選" : "點擊選取";
  const SubIcon = isMarquee ? SquareDashedMousePointer : MousePointer2;
  // Close the popover when the user clicks anywhere outside it.
  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent): void => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <div ref={ref} className="relative inline-flex">
      <button
        className={`btn-ghost text-xs rounded-r-none ${active ? "bg-accent/15 text-accent border border-accent/40" : "border border-transparent"}`}
        onClick={() => onPick(isMarquee ? "select_marquee" : "select")}
        title={`${subLabel}（快捷鍵 ${isMarquee ? "M" : "V"}）`}
      >
        <SubIcon className="h-4 w-4" /> 選取
      </button>
      <button
        className={`btn-ghost text-xs px-1 rounded-l-none border-l-0 ${active ? "bg-accent/15 text-accent border border-accent/40" : "border border-transparent"}`}
        onClick={() => setOpen((v) => !v)}
        title="切換選取模式"
        aria-label="選取模式"
        aria-expanded={open}
      >
        <ChevronDown className="h-3.5 w-3.5" />
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1 z-30 w-44 rounded-md border border-border bg-bg-surface shadow-lg overflow-hidden text-xs">
          <button
            onClick={() => { onPick("select"); setOpen(false); }}
            className={`w-full text-left px-3 py-2 inline-flex items-center justify-between gap-2 hover:bg-bg-hover ${isClick ? "bg-accent/10 text-accent" : ""}`}
          >
            <span className="inline-flex items-center gap-2">
              <MousePointer2 className="h-3.5 w-3.5" />
              點擊選取
            </span>
            <kbd className="text-2xs text-text-muted font-mono">V</kbd>
          </button>
          <button
            onClick={() => { onPick("select_marquee"); setOpen(false); }}
            className={`w-full text-left px-3 py-2 inline-flex items-center justify-between gap-2 hover:bg-bg-hover ${isMarquee ? "bg-accent/10 text-accent" : ""}`}
          >
            <span className="inline-flex items-center gap-2">
              <SquareDashedMousePointer className="h-3.5 w-3.5" />
              框選
            </span>
            <kbd className="text-2xs text-text-muted font-mono">M</kbd>
          </button>
        </div>
      )}
    </div>
  );
}

function DropZone({ onPick, onDrop }: { onPick: () => void; onDrop: (f: File) => void }): React.ReactElement {
  return (
    <div
      className="absolute inset-0 z-10 grid place-items-center cursor-pointer hover:bg-accent/5 transition-colors"
      onClick={onPick}
      onDrop={(e) => {
        e.preventDefault();
        const f = e.dataTransfer.files?.[0];
        if (f) onDrop(f);
      }}
      onDragOver={(e) => e.preventDefault()}
    >
      <div className="text-center">
        <Upload className="h-10 w-10 mx-auto mb-3 text-accent" />
        <div className="text-sm text-text-primary mb-1">拖入或點選樓層平面圖</div>
        <div className="text-2xs text-text-muted">支援 PNG / JPG / PDF（最大 10 MB）— 系統會自動辨識牆 / 房 / 門 / 走道</div>
      </div>
    </div>
  );
}

function WallNode({ wall, selected, onSelect, onPointDown }: {
  wall: LayoutWall; selected: boolean;
  onSelect: () => void;
  onPointDown: (idx: number) => (e: React.MouseEvent) => void;
}): React.ReactElement {
  const [a, b] = wall.points;
  const stroke = wall.kind === "exterior" ? "#e5e7eb" : "#94a3b8";
  return (
    <g data-entity-kind="wall" data-entity-id={wall.id}
      onClick={(e) => { e.stopPropagation(); onSelect(); }}
    >
      <line x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke={stroke} strokeWidth={selected ? 4 : (wall.thickness_px || 3)} />
      {selected && (
        <>
          <circle cx={a.x} cy={a.y} r={6} fill="#00d4ff" stroke="#0b0d12" strokeWidth={2} style={{ cursor: "grab" }} onMouseDown={onPointDown(0)} />
          <circle cx={b.x} cy={b.y} r={6} fill="#00d4ff" stroke="#0b0d12" strokeWidth={2} style={{ cursor: "grab" }} onMouseDown={onPointDown(1)} />
        </>
      )}
    </g>
  );
}

const ROOM_FILLS: Record<LayoutRoom["kind"], string> = {
  office: "rgba(34,197,94,0.12)",
  meeting: "rgba(59,130,246,0.12)",
  lobby: "rgba(0,212,255,0.10)",
  corridor: "rgba(148,163,184,0.10)",
  stair: "rgba(168,85,247,0.12)",
  elevator: "rgba(168,85,247,0.18)",
  restroom: "rgba(20,184,166,0.12)",
  utility: "rgba(100,116,139,0.10)",
  storage: "rgba(180,140,80,0.10)",
  exit: "rgba(239,68,68,0.15)",
  outdoor: "rgba(125,211,252,0.08)",
  unknown: "rgba(148,163,184,0.06)",
};

function RoomNode({ room, selected, onSelect, onPointDown }: {
  room: LayoutRoom; selected: boolean;
  onSelect: () => void;
  onPointDown: (idx: number) => (e: React.MouseEvent) => void;
}): React.ReactElement {
  const points = room.vertices.map((p) => `${p.x},${p.y}`).join(" ");
  const center = room.vertices.reduce(
    (acc, p) => ({ x: acc.x + p.x / room.vertices.length, y: acc.y + p.y / room.vertices.length }),
    { x: 0, y: 0 },
  );
  return (
    <g data-entity-kind="room" data-entity-id={room.id} onClick={(e) => { e.stopPropagation(); onSelect(); }}>
      {/* Room outline doubles as the visible wall stroke for every edge
       *  the model assigned to a room polygon but did NOT also emit as an
       *  explicit walls[] entry. Without this, edges where the model
       *  "consumed" the wall into room.vertices look like the wall is
       *  missing — even though the geometry is intact. Render with the
       *  same dark stroke as interior walls so the building outline
       *  looks complete regardless of which entity holds the edge.
       *  Selection ring (cyan) overrides when the room is picked. */}
      <polygon points={points} fill={ROOM_FILLS[room.kind] ?? ROOM_FILLS.unknown}
        stroke={selected ? "#00d4ff" : "#1f2937"} strokeWidth={selected ? 2 : 2}
        strokeLinejoin="round" />
      {room.label && (
        <text x={center.x} y={center.y} fill="#e5e7eb" fontSize={11} textAnchor="middle" dominantBaseline="middle">
          {room.label}
        </text>
      )}
      {selected && room.vertices.map((v, i) => (
        <circle key={i} cx={v.x} cy={v.y} r={5} fill="#00d4ff" stroke="#0b0d12" strokeWidth={2} style={{ cursor: "grab" }} onMouseDown={onPointDown(i)} />
      ))}
    </g>
  );
}

function DoorNode({ door, selected, onSelect, onPointDown }: {
  door: LayoutDoor; selected: boolean;
  onSelect: () => void;
  onPointDown: (field: "hinge" | "swing_to") => (e: React.MouseEvent) => void;
}): React.ReactElement {
  const dx = door.swing_to.x - door.hinge.x;
  const dy = door.swing_to.y - door.hinge.y;
  const length = Math.hypot(dx, dy) || 1;
  const ax = door.hinge.x + dx / 2;
  const ay = door.hinge.y + dy / 2;
  return (
    <g data-entity-kind="door" data-entity-id={door.id} onClick={(e) => { e.stopPropagation(); onSelect(); }}>
      {/* Swing direction: subtle when idle (~30% opacity, thin), prominent
       *  when this door is selected. Used to be 80% opacity 2px always,
       *  which created a sea of cyan leader-lines on busy plans. */}
      <line x1={door.hinge.x} y1={door.hinge.y} x2={door.swing_to.x} y2={door.swing_to.y}
        stroke="#00d4ff" strokeWidth={selected ? 3 : 1.5} strokeOpacity={selected ? 0.85 : 0.3} />
      <circle cx={door.hinge.x} cy={door.hinge.y} r={selected ? 4 : 3} fill="#00d4ff" fillOpacity={selected ? 1 : 0.6} />
      {door.label && <text x={ax + 8} y={ay - 6} fill="#00d4ff" fontSize={11}>{door.label}</text>}
      {selected && (
        <>
          <circle cx={door.hinge.x} cy={door.hinge.y} r={6} fill="#00d4ff" stroke="#0b0d12" strokeWidth={2} style={{ cursor: "grab" }} onMouseDown={onPointDown("hinge")} />
          <circle cx={door.swing_to.x} cy={door.swing_to.y} r={6} fill="#00d4ff" stroke="#0b0d12" strokeWidth={2} style={{ cursor: "grab" }} onMouseDown={onPointDown("swing_to")} />
          {/* Swing-radius px readout — only visible on the selected door so
           *  busy plans aren't covered in floating numbers. Useful when an
           *  operator is fine-tuning a single door's geometry. */}
          <text x={door.hinge.x} y={door.hinge.y - 12} fill="#94a3b8" fontSize={9} textAnchor="middle">⟳ {Math.round(length)}px</text>
        </>
      )}
    </g>
  );
}

function SelectionInspector({ state, onChange, onClose }: {
  state: State; onChange: (layout: NormalizedLayoutDto) => void; onClose: () => void;
}): React.ReactElement | null {
  const sel = state.selection;
  if (!sel) return null;
  if (sel.kind === "wall") {
    const wall = state.layout.walls.find((w) => w.id === sel.id);
    if (!wall) return null;
    return (
      <div className="card p-3 space-y-2">
        <div className="text-2xs font-semibold text-text-secondary flex items-center justify-between">
          <span>選中：{wall.id}（牆）</span>
          <button className="btn-ghost text-2xs" onClick={onClose}>關閉</button>
        </div>
        <label className="block text-2xs">
          <span className="text-text-secondary">類型</span>
          <select className="input mt-1 text-sm" value={wall.kind} onChange={(e) => {
            onChange({ ...state.layout, walls: state.layout.walls.map((w) => w.id === wall.id ? { ...w, kind: e.target.value as "exterior" | "interior" } : w) });
          }}>
            <option value="interior">內隔牆</option>
            <option value="exterior">外牆</option>
          </select>
        </label>
        <label className="block text-2xs">
          <span className="text-text-secondary">線寬（px）</span>
          <input type="number" className="input mt-1 text-sm" value={wall.thickness_px ?? 3} onChange={(e) => {
            onChange({ ...state.layout, walls: state.layout.walls.map((w) => w.id === wall.id ? { ...w, thickness_px: Number(e.target.value) || 3 } : w) });
          }} />
        </label>
      </div>
    );
  }
  if (sel.kind === "room") {
    const room = state.layout.rooms.find((r) => r.id === sel.id);
    if (!room) return null;
    return (
      <div className="card p-3 space-y-2">
        <div className="text-2xs font-semibold text-text-secondary flex items-center justify-between">
          <span>選中：{room.id}（房間）</span>
          <button className="btn-ghost text-2xs" onClick={onClose}>關閉</button>
        </div>
        <label className="block text-2xs">
          <span className="text-text-secondary">名稱</span>
          <input className="input mt-1 text-sm" value={room.label ?? ""} onChange={(e) => {
            onChange({ ...state.layout, rooms: state.layout.rooms.map((r) => r.id === room.id ? { ...r, label: e.target.value || null } : r) });
          }} placeholder="例：會議室 A" />
        </label>
        <label className="block text-2xs">
          <span className="text-text-secondary">類型</span>
          <select className="input mt-1 text-sm" value={room.kind} onChange={(e) => {
            onChange({ ...state.layout, rooms: state.layout.rooms.map((r) => r.id === room.id ? { ...r, kind: e.target.value as LayoutRoom["kind"] } : r) });
          }}>
            <option value="office">辦公室</option>
            <option value="meeting">會議室</option>
            <option value="lobby">大廳</option>
            <option value="corridor">走道</option>
            <option value="stair">樓梯</option>
            <option value="elevator">電梯</option>
            <option value="restroom">洗手間</option>
            <option value="utility">設備間</option>
            <option value="storage">儲藏</option>
            <option value="exit">出口</option>
            <option value="outdoor">戶外</option>
            <option value="unknown">未分類</option>
          </select>
        </label>
      </div>
    );
  }
  if (sel.kind === "door") {
    const door = state.layout.doors.find((d) => d.id === sel.id);
    if (!door) return null;
    return (
      <div className="card p-3 space-y-2">
        <div className="text-2xs font-semibold text-text-secondary flex items-center justify-between">
          <span>選中：{door.id}（門）</span>
          <button className="btn-ghost text-2xs" onClick={onClose}>關閉</button>
        </div>
        <label className="block text-2xs">
          <span className="text-text-secondary">標示文字</span>
          <input className="input mt-1 text-sm" value={door.label ?? ""} onChange={(e) => {
            onChange({ ...state.layout, doors: state.layout.doors.map((d) => d.id === door.id ? { ...d, label: e.target.value || null } : d) });
          }} placeholder="例：緊急出口" />
        </label>
      </div>
    );
  }
  return null;
}

// ── Export helpers ───────────────────────────────────────────────────────

function downloadBlob(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function downloadSvg(layout: NormalizedLayoutDto, name: string): void {
  const w = layout.image_size.width;
  const h = layout.image_size.height;
  const wallSvg = layout.walls.map((wl) => {
    const [a, b] = wl.points;
    return `<line x1="${a.x}" y1="${a.y}" x2="${b.x}" y2="${b.y}" stroke="${wl.kind === "exterior" ? "#111" : "#666"}" stroke-width="${wl.thickness_px ?? 3}"/>`;
  }).join("");
  const roomSvg = layout.rooms.map((r) => {
    const pts = r.vertices.map((p) => `${p.x},${p.y}`).join(" ");
    // Room polygon outlined like a wall so edges the model put on
    // room.vertices (but not in walls[]) still render as a visible
    // partition. Mirrors the on-canvas treatment in <RoomShape>.
    return `<polygon points="${pts}" fill="rgba(200,200,200,0.2)" stroke="#1f2937" stroke-width="2" stroke-linejoin="round"/>${r.label ? `<text x="${r.vertices[0]?.x ?? 0}" y="${r.vertices[0]?.y ?? 0}" font-size="11">${escape(r.label)}</text>` : ""}`;
  }).join("");
  const doorSvg = layout.doors.map((d) =>
    `<line x1="${d.hinge.x}" y1="${d.hinge.y}" x2="${d.swing_to.x}" y2="${d.swing_to.y}" stroke="#0bb" stroke-width="2"/>`,
  ).join("");
  const svg = `<?xml version="1.0"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">
  ${roomSvg}
  ${wallSvg}
  ${doorSvg}
</svg>`;
  downloadBlob(new Blob([svg], { type: "image/svg+xml" }), name);
}

function escape(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);
}

/** True when the rectangle covers a non-zero area. Drag-and-release in
 *  the same spot is treated as an empty marquee — caller falls back to
 *  "clear" so it doesn't accidentally select everything within 1 px. */
function rectIsValid(r: MarqueeRect): boolean {
  return Math.abs(r.x2 - r.x1) >= 2 && Math.abs(r.y2 - r.y1) >= 2;
}

/** Normalise a possibly-inverted rect (drag from bottom-right to
 *  top-left) into [min, max] coordinates. */
function normaliseRect(r: MarqueeRect): { minX: number; minY: number; maxX: number; maxY: number } {
  return {
    minX: Math.min(r.x1, r.x2),
    minY: Math.min(r.y1, r.y2),
    maxX: Math.max(r.x1, r.x2),
    maxY: Math.max(r.y1, r.y2),
  };
}

function pointInRect(p: { x: number; y: number }, b: { minX: number; minY: number; maxX: number; maxY: number }): boolean {
  return p.x >= b.minX && p.x <= b.maxX && p.y >= b.minY && p.y <= b.maxY;
}

/** Pick the "anchor" point of each layout entity for marquee hit-testing.
 *  Rooms use the polygon's first vertex when no centroid helper is in
 *  scope (the `lib/navigation/geometry.ts` helper isn't imported here
 *  to keep this file standalone for now); polygons drawn larger than
 *  the marquee still get picked because the rect always intersects at
 *  least one vertex when fully containing the room. Walls use the
 *  midpoint so a marquee covering the segment picks them even when
 *  endpoints lie outside. */
function entitiesInsideRect(layout: NormalizedLayoutDto, rect: MarqueeRect): SelectionId[] {
  if (!rectIsValid(rect)) return [];
  const b = normaliseRect(rect);
  const out: SelectionId[] = [];
  for (const w of layout.walls ?? []) {
    const mid = { x: (w.points[0].x + w.points[1].x) / 2, y: (w.points[0].y + w.points[1].y) / 2 };
    if (pointInRect(mid, b)) out.push({ kind: "wall", id: w.id });
  }
  for (const r of layout.rooms ?? []) {
    if (!r.vertices || r.vertices.length === 0) continue;
    // Picked if any vertex is inside the rect — keeps large rooms
    // detectable even when only a corner overlaps the marquee.
    if (r.vertices.some((v) => pointInRect(v, b))) out.push({ kind: "room", id: r.id });
  }
  for (const d of layout.doors ?? []) {
    if (d.hinge && pointInRect(d.hinge, b)) out.push({ kind: "door", id: d.id });
  }
  for (const c of layout.corridors ?? []) {
    if (!c.centerline || c.centerline.length === 0) continue;
    if (c.centerline.some((p) => pointInRect(p, b))) out.push({ kind: "corridor", id: c.id });
  }
  for (const ep of layout.entry_points ?? []) {
    if (ep.point && pointInRect(ep.point, b)) out.push({ kind: "entry_point", id: ep.id });
  }
  for (const lbl of layout.labels ?? []) {
    if (lbl.point && pointInRect(lbl.point, b)) out.push({ kind: "label", id: lbl.id });
  }
  return out;
}
